import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowLeft,
  CloudUpload,
  Database,
  Eye,
  ExternalLink,
  FolderGit2,
  Package,
  Plus,
  RefreshCcw,
  Rocket,
  Save,
  Server,
  ShieldCheck,
  Tags,
  Trash2,
  Undo2,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { deploymentDashboardApi } from '../../services/deploymentApi';
import { useAuth } from '../../contexts/AuthContext';
import { DeploymentsExplorer } from './DeploymentsExplorer';
import { SetupPanel } from './SetupPanel';
import {
  DeploymentCustomer,
  DeploymentOverview,
  DeploymentRecord,
  DeploymentReleaseSelection,
  RepositoryDeploymentProfile,
  DeploymentServer,
  DeploymentTargetRecord,
  GitLabReleaseOptions,
  ParameterReference,
  RepositoryCatalogRecord,
  RolloutMetadataReadinessReport,
  RuntimeConfigInstanceRecord,
  RuntimeConfigTemplateRecord,
  SecretReference,
} from '../../types/deployment';
import {
  validatePipelineVariablesJson,
  validateRuntimeConfigVariablesJson,
} from '../../utils/deploymentJsonValidation';
import { formatDeploymentModeLabel, resolveRollbackMode } from '../../utils/deploymentMode';

interface DeploymentDashboardProps {
  onBack: () => void;
}

type DashboardTab =
  | 'overview'
  | 'customers'
  | 'servers'
  | 'repositories'
  | 'targets'
  | 'configs'
  | 'deployments'
  | 'setup';

type CustomerFormState = {
  id: string;
  name: string;
  slug: string;
  description: string;
  status: string;
  tags: string;
};

type SecretFormState = {
  id: string;
  name: string;
  description: string;
  gcpProjectId: string;
  secretId: string;
  secretVersion: string;
  referenceKey: string;
  secretType: string;
  customer: string;
  repository: string;
  server: string;
  environment: string;
  allowGlobalFallback: boolean;
  allowHostFileMaterialization: boolean;
  labels: string;
  isActive: boolean;
};

type ParameterFormState = {
  id: string;
  name: string;
  description: string;
  gcpProjectId: string;
  location: string;
  parameterId: string;
  parameterVersion: string;
  referenceKey: string;
  parameterFormat: string;
  customer: string;
  repository: string;
  server: string;
  environment: string;
  allowGlobalFallback: boolean;
  labels: string;
  isActive: boolean;
};

type ServerFormState = {
  id: string;
  customer: string;
  name: string;
  slug: string;
  hostname: string;
  ipAddress: string;
  sshPort: string;
  description: string;
  environments: string;
  tags: string;
  sshUser: string;
  sshPrivateKey: string;
  sshKnownHosts: string;
  sshPassword: string;
  deployPath: string;
  composeFilePath: string;
  composeEnvFile: string;
  composeNetworkName: string;
  dockerServiceName: string;
  runtimeEnvFilePath: string;
  remoteServiceRestartCommand: string;
  healthcheckUrl: string;
  privilegeModel: string;
  osFamily: string;
  packageManager: string;
  nginxManaged: boolean;
  tlsEnabled: boolean;
  supportedModes: string;
  gcpCredentialsBasePath: string;
  allowedCredentialCopyTargetIds: string;
};

type TemplateFormState = {
  id: string;
  name: string;
  slug: string;
  description: string;
  customer: string;
  server: string;
  repository: string;
  environment: string;
  tags: string;
  variablesJson: string;
  isActive: boolean;
};

type InstanceFormState = {
  id: string;
  name: string;
  template: string;
  customer: string;
  server: string;
  repository: string;
  deploymentTarget: string;
  environment: string;
  overridesJson: string;
  isActive: boolean;
};

type TargetFormState = {
  id: string;
  name: string;
  slug: string;
  customer: string;
  server: string;
  repository: string;
  environment: string;
  deploymentStrategy: string;
  deployJobName: string;
  setupJobName: string;
  setupFamily: string;
  allowedDeploymentModes: string;
  defaultDeploymentMode: string;
  modeJobMappingJson: string;
  versionStrategy: string;
  defaultConfigTemplate: string;
  defaultConfigInstance: string;
  stackBundleKey: string;
  stackPhase: string;
  dependencyRepositoryKeys: string;
  healthcheckProfile: string;
  healthcheckUrl: string;
  enabled: boolean;
  tags: string;
  deployPath: string;
  composeFilePath: string;
  composeEnvFile: string;
  composeNetworkName: string;
  dockerServiceName: string;
  runtimeEnvFilePath: string;
  remoteServiceRestartCommand: string;
  setupDomain: string;
  setupAppUser: string;
  setupPublicPort: string;
  setupPrivatePort: string;
  setupNginxUpstreamPort: string;
  setupTlsEnabled: string;
  setupNginxSiteName: string;
  acmeEmail: string;
  gcpProfileScope: string;
  gcpAppCode: string;
  gcpCustomerCode: string;
  gcpRobotCode: string;
  configParameterRole: string;
  credentialsHostPath: string;
  catalogPath: string;
  publicConfigParameterId: string;
  pipelineVariablesJson: string;
};

type RepositoryFormState = {
  gitlabProjectId: string;
  pathWithNamespace: string;
};

type DeployFormState = {
  deploymentTargetId: string;
  deploymentMode: string;
  sourceKey: string;
  runtimeConfigTemplateId: string;
  runtimeConfigInstanceId: string;
  reason: string;
};

const initialCustomerForm: CustomerFormState = {
  id: '',
  name: '',
  slug: '',
  description: '',
  status: 'active',
  tags: '',
};

const initialSecretForm: SecretFormState = {
  id: '',
  name: '',
  description: '',
  gcpProjectId: '',
  secretId: '',
  secretVersion: 'latest',
  referenceKey: '',
  secretType: 'custom',
  customer: '',
  repository: '',
  server: '',
  environment: '',
  allowGlobalFallback: true,
  allowHostFileMaterialization: false,
  labels: '',
  isActive: true,
};

const initialParameterForm: ParameterFormState = {
  id: '',
  name: '',
  description: '',
  gcpProjectId: '',
  location: 'global',
  parameterId: '',
  parameterVersion: 'latest',
  referenceKey: '',
  parameterFormat: 'text',
  customer: '',
  repository: '',
  server: '',
  environment: '',
  allowGlobalFallback: true,
  labels: '',
  isActive: true,
};

const initialServerForm: ServerFormState = {
  id: '',
  customer: '',
  name: '',
  slug: '',
  hostname: '',
  ipAddress: '',
  sshPort: '22',
  description: '',
  environments: 'dev, qa, staging, prod',
  tags: '',
  sshUser: '',
  sshPrivateKey: '',
  sshKnownHosts: '',
  sshPassword: '',
  deployPath: '',
  composeFilePath: '',
  composeEnvFile: '',
  composeNetworkName: '',
  dockerServiceName: '',
  runtimeEnvFilePath: '',
  remoteServiceRestartCommand: '',
  healthcheckUrl: '',
  privilegeModel: 'unknown',
  osFamily: 'unknown',
  packageManager: 'unknown',
  nginxManaged: false,
  tlsEnabled: false,
  supportedModes: '',
  gcpCredentialsBasePath: '',
  allowedCredentialCopyTargetIds: '',
};

const initialTemplateForm: TemplateFormState = {
  id: '',
  name: '',
  slug: '',
  description: '',
  customer: '',
  server: '',
  repository: '',
  environment: '',
  tags: '',
  variablesJson: '[]',
  isActive: true,
};

const initialInstanceForm: InstanceFormState = {
  id: '',
  name: '',
  template: '',
  customer: '',
  server: '',
  repository: '',
  deploymentTarget: '',
  environment: '',
  overridesJson: '[]',
  isActive: true,
};

const initialTargetForm: TargetFormState = {
  id: '',
  name: '',
  slug: '',
  customer: '',
  server: '',
  repository: '',
  environment: 'dev',
  deploymentStrategy: 'template-manual',
  deployJobName: '',
  setupJobName: '',
  setupFamily: '',
  allowedDeploymentModes: '',
  defaultDeploymentMode: '',
  modeJobMappingJson: '[]',
  versionStrategy: 'auto',
  defaultConfigTemplate: '',
  defaultConfigInstance: '',
  stackBundleKey: '',
  stackPhase: '',
  dependencyRepositoryKeys: '',
  healthcheckProfile: '',
  healthcheckUrl: '',
  enabled: true,
  tags: '',
  deployPath: '',
  composeFilePath: '',
  composeEnvFile: '',
  composeNetworkName: '',
  dockerServiceName: '',
  runtimeEnvFilePath: '',
  remoteServiceRestartCommand: '',
  setupDomain: '',
  setupAppUser: '',
  setupPublicPort: '',
  setupPrivatePort: '',
  setupNginxUpstreamPort: '',
  setupTlsEnabled: '',
  setupNginxSiteName: '',
  acmeEmail: '',
  gcpProfileScope: 'shared',
  gcpAppCode: '',
  gcpCustomerCode: '',
  gcpRobotCode: '',
  configParameterRole: 'config',
  credentialsHostPath: '',
  catalogPath: '',
  publicConfigParameterId: '',
  pipelineVariablesJson: '{}',
};

const initialRepositoryForm: RepositoryFormState = {
  gitlabProjectId: '',
  pathWithNamespace: '',
};

const initialDeployForm: DeployFormState = {
  deploymentTargetId: '',
  deploymentMode: '',
  sourceKey: '',
  runtimeConfigTemplateId: '',
  runtimeConfigInstanceId: '',
  reason: '',
};

const renderIcon = (IconComponent: LucideIcon, size = 18) => (
  <IconComponent size={size} />
);

const stringifyJson = (value: unknown) => JSON.stringify(value ?? [], null, 2);

const asNamedEntity = <T extends { _id: string; name?: string; slug?: string }>(value: string | T | null | undefined) => {
  if (!value || typeof value === 'string') {
    return null;
  }

  return value;
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleString();
};

const DEFAULT_REPOSITORY_OVERRIDE_REASON = 'Remove stale break-glass override';

type RepositoryOverrideField = keyof RepositoryDeploymentProfile;

const REPOSITORY_OVERRIDE_FIELDS: RepositoryOverrideField[] = [
  'setupFamily',
  'allowedDeploymentModes',
  'defaultDeploymentMode',
  'stackBundleKey',
  'stackPhase',
  'dependencyRepositoryKeys',
  'healthcheckProfile',
];

const isRepositoryOverrideField = (field: string): field is RepositoryOverrideField => (
  REPOSITORY_OVERRIDE_FIELDS.includes(field as RepositoryOverrideField)
);

const getRepositoryOverrideClearValue = (field: RepositoryOverrideField) => {
  switch (field) {
    case 'allowedDeploymentModes':
    case 'dependencyRepositoryKeys':
      return [];
    case 'stackPhase':
      return null;
    default:
      return '';
  }
};

const formatOverrideValue = (value: unknown) => {
  if (value === undefined) {
    return '(not set)';
  }

  if (typeof value === 'string') {
    return value || '(empty string)';
  }

  return JSON.stringify(value, null, 2);
};

const getMetadataString = (metadata: Record<string, unknown> | undefined, key: string) => {
  const value = metadata?.[key];
  return typeof value === 'string' ? value : '';
};

const getMetadataBooleanString = (metadata: Record<string, unknown> | undefined, key: string) => {
  const value = metadata?.[key];
  return value === undefined || value === null ? '' : String(value);
};

const getRequestFailureMessage = (error: unknown, fallbackMessage: string, timeoutMessage?: string) => {
  const err = error as {
    code?: string;
    message?: string;
    response?: {
      data?: {
        message?: string;
      };
    };
  };

  if (err?.response?.data?.message) {
    return err.response.data.message;
  }

  if (timeoutMessage && (err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT' || /timeout/i.test(err?.message || ''))) {
    return timeoutMessage;
  }

  if (err?.message) {
    return err.message;
  }

  return fallbackMessage;
};

const parseModeJobMappingJson = (value: string): { parsedValue: Array<Record<string, unknown>> | null; error: string } => {
  try {
    const parsed = JSON.parse(value || '[]');
    if (!Array.isArray(parsed)) {
      return { parsedValue: null, error: 'Mode job mapping must be a JSON array' };
    }

    return { parsedValue: parsed, error: '' };
  } catch {
    return { parsedValue: null, error: 'Mode job mapping must be valid JSON' };
  }
};

const resolveDefaultDeploymentMode = (target: DeploymentTargetRecord | null | undefined) => (
  target?.defaultDeploymentMode || target?.allowedDeploymentModes?.[0] || ''
);

const resolveDeploymentTargetForRecord = (
  deploymentRecord: DeploymentRecord,
  targets: DeploymentTargetRecord[]
) => {
  const deploymentTarget = deploymentRecord.deploymentTarget;
  if (deploymentTarget && typeof deploymentTarget !== 'string') {
    return deploymentTarget;
  }

  return targets.find((target) => target._id === deploymentTarget) || null;
};

const targetSupportsDockerRestart = (target: DeploymentTargetRecord | null | undefined) => (
  Boolean(
    target
    && (
      target.defaultDeploymentMode === 'docker'
      || target.setupFamily === 'docker'
      || target.allowedDeploymentModes?.includes('docker')
    )
  )
);

const statusColor = (status?: string) => {
  switch (status) {
    case 'success':
      return 'success';
    case 'failed':
      return 'error';
    case 'running':
      return 'info';
    case 'manual_job_pending':
      return 'warning';
    default:
      return 'default';
  }
};

const DEPLOYMENT_FAILURE_CLASS_SUMMARY: Record<string, string> = {
  'lock-conflict': 'Another setup or deploy already holds the server lock.',
  'host-unreachable': 'The server could not be reached over SSH.',
  'missing-sudo': 'The deploy user does not have non-interactive sudo.',
  'low-disk': 'The target host does not have enough free disk.',
  'low-memory': 'The target host does not have enough free memory.',
  'port-conflict': 'A required host port is already in use.',
  'host-namespace-conflict': 'A host path, compose identifier, or Nginx namespace conflicts with another app.',
  'healthcheck-failed': 'The service did not become healthy before deployment validation timed out.',
  'rate-limited': 'A control-plane call was rate-limited and needs a retry.',
  'quota-exceeded': 'A control-plane quota or resource limit was exceeded.',
  'control-plane-unavailable': 'A control-plane dependency was temporarily unavailable.',
};

const formatFailureClassSummary = (failureClass: string): string => {
  if (!failureClass) {
    return '';
  }

  return DEPLOYMENT_FAILURE_CLASS_SUMMARY[failureClass]
    || failureClass.split('-').join(' ');
};

const formatProgressStatusLabel = (status?: string) => {
  if (!status) {
    return '-';
  }

  return status.charAt(0).toUpperCase() + status.slice(1);
};

const getDeploymentFailureClass = (deploymentRecord: DeploymentRecord): string => String(
  deploymentRecord.setupProgress?.failureClass
  || deploymentRecord.metadata?.failureClass
  || ''
).trim();

const getDeploymentFailureMessage = (deploymentRecord: DeploymentRecord): string => String(
  deploymentRecord.setupProgress?.failedStepMessage
  || deploymentRecord.errorMessage
  || ''
).trim();

const getDeploymentProgressSummaryLines = (deploymentRecord: DeploymentRecord): string[] => {
  const progress = deploymentRecord.setupProgress;
  if (!progress) {
    return [];
  }

  const lines: string[] = [];

  if (progress.currentStep) {
    if (progress.currentStepStatus === 'running') {
      lines.push(`Current step: ${progress.currentStep}`);
    } else if (progress.currentStepStatus) {
      lines.push(`Last step: ${progress.currentStep} (${progress.currentStepStatus})`);
    } else {
      lines.push(`Current step: ${progress.currentStep}`);
    }
  }

  const completedSteps = progress.completedSteps ?? [];
  if (completedSteps.length > 0) {
    const recentSteps = completedSteps.slice(-3);
    lines.push(
      completedSteps.length > recentSteps.length
        ? `Completed (${completedSteps.length}): ${recentSteps.join(', ')}`
        : `Completed: ${recentSteps.join(', ')}`
    );
  }

  if (progress.rawFallback) {
    lines.push('GitLab trace did not include structured progress markers.');
  }

  return lines;
};

const metadataReadinessColor = (issues: Array<{ severity: 'error' | 'warning' }> = []) => {
  if (issues.some((issue) => issue.severity === 'error')) {
    return 'error';
  }

  if (issues.some((issue) => issue.severity === 'warning')) {
    return 'warning';
  }

  return 'success';
};

const repositoryReadinessChipColor = (
  readiness?: { ready?: boolean; issues?: Array<{ severity: 'error' | 'warning' }> } | null
) => {
  if (readiness == null) {
    return 'default';
  }

  return metadataReadinessColor(readiness.issues || []);
};

const repositoryReadinessChipLabel = (
  readiness?: { ready?: boolean } | null
) => {
  if (readiness == null) {
    return 'Unknown';
  }

  return readiness.ready === false ? 'Needs attention' : 'Ready';
};

const REPOSITORY_TABLE_INITIAL_COLUMN_WIDTHS: number[] = [
  260, 200, 120, 120, 120, 120, 100, 160, 140,
];

const REPOSITORY_TABLE_MIN_COLUMN_WIDTH = 56;
const REPOSITORY_TABLE_MAX_COLUMN_WIDTH = 1200;
const REPOSITORY_TABLE_COLUMN_KEYBOARD_STEP = 16;

const clampRepositoryTableColumnWidth = (width: number) =>
  Math.min(
    REPOSITORY_TABLE_MAX_COLUMN_WIDTH,
    Math.max(REPOSITORY_TABLE_MIN_COLUMN_WIDTH, Math.round(width)),
  );

const REPOSITORY_TABLE_COLUMN_HEADERS: Array<{ label: string; align?: 'right' }> = [
  { label: 'Repository' },
  { label: 'Setup Profile' },
  { label: 'Artifact Kind' },
  { label: 'Latest Tag' },
  { label: 'Latest Package' },
  { label: 'Latest Image' },
  { label: 'Latest SHA' },
  { label: 'Readiness' },
  { label: 'Actions', align: 'right' },
];

const DeploymentDashboard: React.FC<DeploymentDashboardProps> = ({ onBack }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState<DashboardTab>('overview');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState<DeploymentOverview | null>(null);
  const [rolloutMetadataReadiness, setRolloutMetadataReadiness] = useState<RolloutMetadataReadinessReport | null>(null);
  const [customers, setCustomers] = useState<DeploymentCustomer[]>([]);
  const [secretReferences, setSecretReferences] = useState<SecretReference[]>([]);
  const [parameterReferences, setParameterReferences] = useState<ParameterReference[]>([]);
  const [servers, setServers] = useState<DeploymentServer[]>([]);
  const [repositories, setRepositories] = useState<RepositoryCatalogRecord[]>([]);
  const [repositoryTableColumnWidths, setRepositoryTableColumnWidths] = useState<number[]>(() => [
    ...REPOSITORY_TABLE_INITIAL_COLUMN_WIDTHS,
  ]);
  const repositoryTableColumnWidthsRef = useRef(repositoryTableColumnWidths);
  repositoryTableColumnWidthsRef.current = repositoryTableColumnWidths;

  const repositoryTableResizeCleanupRef = useRef<(() => void) | null>(null);
  const repositoryTableResizeRafRef = useRef<number | null>(null);
  const repositoryTableResizeLatestXRef = useRef(0);
  const repositoryTableResizeRafPendingRef = useRef(false);
  const repositoryTableResizeMoveHandlerRef = useRef<((moveEvent: MouseEvent) => void) | null>(null);
  const repositoryTableResizeUpHandlerRef = useRef<(() => void) | null>(null);

  const detachRepositoryTableResizeDocumentListeners = useCallback(() => {
    if (repositoryTableResizeRafRef.current != null) {
      cancelAnimationFrame(repositoryTableResizeRafRef.current);
      repositoryTableResizeRafRef.current = null;
    }
    repositoryTableResizeRafPendingRef.current = false;
    const moveHandler = repositoryTableResizeMoveHandlerRef.current;
    const upHandler = repositoryTableResizeUpHandlerRef.current;
    if (moveHandler) {
      document.removeEventListener('mousemove', moveHandler);
      repositoryTableResizeMoveHandlerRef.current = null;
    }
    if (upHandler) {
      document.removeEventListener('mouseup', upHandler);
      repositoryTableResizeUpHandlerRef.current = null;
    }
    document.body.style.removeProperty('cursor');
    document.body.style.removeProperty('user-select');
    repositoryTableResizeCleanupRef.current = null;
  }, []);

  const nudgeRepositoryTableColumnWidth = useCallback((columnIndex: number, deltaPx: number) => {
    setRepositoryTableColumnWidths((prev) => {
      const next = [...prev];
      next[columnIndex] = clampRepositoryTableColumnWidth(prev[columnIndex] + deltaPx);
      return next;
    });
  }, []);

  const beginRepositoryTableColumnResize = useCallback((columnIndex: number) => (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    repositoryTableResizeCleanupRef.current?.();

    const startX = event.clientX;
    const startWidths = [...repositoryTableColumnWidthsRef.current];

    repositoryTableResizeLatestXRef.current = startX;
    repositoryTableResizeRafPendingRef.current = false;

    const onMove = (moveEvent: MouseEvent) => {
      repositoryTableResizeLatestXRef.current = moveEvent.clientX;
      if (repositoryTableResizeRafPendingRef.current) {
        return;
      }
      repositoryTableResizeRafPendingRef.current = true;
      repositoryTableResizeRafRef.current = requestAnimationFrame(() => {
        repositoryTableResizeRafRef.current = null;
        repositoryTableResizeRafPendingRef.current = false;
        const delta = repositoryTableResizeLatestXRef.current - startX;
        setRepositoryTableColumnWidths((prev) => {
          const next = [...prev];
          next[columnIndex] = clampRepositoryTableColumnWidth(startWidths[columnIndex] + delta);
          return next;
        });
      });
    };

    const endRepositoryTableResizeSession = () => {
      const delta = repositoryTableResizeLatestXRef.current - startX;
      setRepositoryTableColumnWidths((prev) => {
        const next = [...prev];
        next[columnIndex] = clampRepositoryTableColumnWidth(startWidths[columnIndex] + delta);
        return next;
      });
      detachRepositoryTableResizeDocumentListeners();
    };

    const onUp = () => {
      endRepositoryTableResizeSession();
    };

    repositoryTableResizeMoveHandlerRef.current = onMove;
    repositoryTableResizeUpHandlerRef.current = onUp;
    repositoryTableResizeCleanupRef.current = endRepositoryTableResizeSession;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [detachRepositoryTableResizeDocumentListeners]);

  useEffect(() => () => {
    detachRepositoryTableResizeDocumentListeners();
  }, [detachRepositoryTableResizeDocumentListeners]);

  const [targets, setTargets] = useState<DeploymentTargetRecord[]>([]);
  const [runtimeTemplates, setRuntimeTemplates] = useState<RuntimeConfigTemplateRecord[]>([]);
  const [runtimeInstances, setRuntimeInstances] = useState<RuntimeConfigInstanceRecord[]>([]);
  const [deployments, setDeployments] = useState<DeploymentRecord[]>([]);
  const [deploymentExplorerRefreshKey, setDeploymentExplorerRefreshKey] = useState(0);
  const [releaseOptionsByRepository, setReleaseOptionsByRepository] = useState<Record<string, GitLabReleaseOptions>>({});
  const [setupNotification, setSetupNotification] = useState<{
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [secretDialogOpen, setSecretDialogOpen] = useState(false);
  const [serverDialogOpen, setServerDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [instanceDialogOpen, setInstanceDialogOpen] = useState(false);
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [repositoryDialogOpen, setRepositoryDialogOpen] = useState(false);
  const [repositoryOverrideDialogRepositoryId, setRepositoryOverrideDialogRepositoryId] = useState('');
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [restartDialogOpen, setRestartDialogOpen] = useState(false);
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [parameterDialogOpen, setParameterDialogOpen] = useState(false);

  const [customerForm, setCustomerForm] = useState<CustomerFormState>(initialCustomerForm);
  const [secretForm, setSecretForm] = useState<SecretFormState>(initialSecretForm);
  const [parameterForm, setParameterForm] = useState<ParameterFormState>(initialParameterForm);
  const [serverForm, setServerForm] = useState<ServerFormState>(initialServerForm);
  const [templateForm, setTemplateForm] = useState<TemplateFormState>(initialTemplateForm);
  const [instanceForm, setInstanceForm] = useState<InstanceFormState>(initialInstanceForm);
  const [targetForm, setTargetForm] = useState<TargetFormState>(initialTargetForm);
  const [repositoryForm, setRepositoryForm] = useState<RepositoryFormState>(initialRepositoryForm);
  const [repositoryOverrideReason, setRepositoryOverrideReason] = useState(DEFAULT_REPOSITORY_OVERRIDE_REASON);
  const [deployForm, setDeployForm] = useState<DeployFormState>(initialDeployForm);
  const [restartDeploymentRecord, setRestartDeploymentRecord] = useState<DeploymentRecord | null>(null);
  const [restartReason, setRestartReason] = useState('');
  const [rollbackDeploymentRecord, setRollbackDeploymentRecord] = useState<DeploymentRecord | null>(null);
  const [rollbackDeploymentMode, setRollbackDeploymentMode] = useState('');
  const [rollbackReason, setRollbackReason] = useState('');
  const [selectedDeploymentId, setSelectedDeploymentId] = useState('');
  const [deploymentDetailsLoading, setDeploymentDetailsLoading] = useState(false);
  const deploymentDetailsRequestRef = useRef(0);

  const templateVariablesValidation = useMemo(
    () => validateRuntimeConfigVariablesJson(templateForm.variablesJson),
    [templateForm.variablesJson]
  );
  const instanceOverridesValidation = useMemo(
    () => validateRuntimeConfigVariablesJson(instanceForm.overridesJson),
    [instanceForm.overridesJson]
  );
  const targetPipelineVariablesValidation = useMemo(
    () => validatePipelineVariablesJson(targetForm.pipelineVariablesJson),
    [targetForm.pipelineVariablesJson]
  );
  const targetModeJobMappingValidation = useMemo(
    () => parseModeJobMappingJson(targetForm.modeJobMappingJson),
    [targetForm.modeJobMappingJson]
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [
        overviewResponse,
        readinessResponse,
        customerResponse,
        secretResponse,
        parameterResponse,
        serverResponse,
        repositoryResponse,
        targetResponse,
        templateResponse,
        instanceResponse,
      ] = await Promise.all([
        deploymentDashboardApi.getOverview(),
        deploymentDashboardApi.getRolloutMetadataReadiness(),
        deploymentDashboardApi.getCustomers(),
        deploymentDashboardApi.getSecretReferences(),
        deploymentDashboardApi.getParameterReferences(),
        deploymentDashboardApi.getServers(),
        deploymentDashboardApi.getRepositories(),
        deploymentDashboardApi.getDeploymentTargets(),
        deploymentDashboardApi.getRuntimeConfigTemplates(),
        deploymentDashboardApi.getRuntimeConfigInstances(),
      ]);

      setOverview(overviewResponse.data);
      setRolloutMetadataReadiness(readinessResponse.data);
      setCustomers(customerResponse.data);
      setSecretReferences(secretResponse.data);
      setParameterReferences(parameterResponse.data);
      setServers(serverResponse.data);
      setRepositories(repositoryResponse.data);
      setTargets(targetResponse.data);
      setRuntimeTemplates(templateResponse.data);
      setRuntimeInstances(instanceResponse.data);
    } catch (err: unknown) {
      const message = getRequestFailureMessage(err, 'Failed to load deployment dashboard');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshTargets = useCallback(async () => {
    const targetResponse = await deploymentDashboardApi.getDeploymentTargets();
    setTargets(targetResponse.data);
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const selectedDeploymentTarget = useMemo(
    () => targets.find((target) => target._id === deployForm.deploymentTargetId) || null,
    [deployForm.deploymentTargetId, targets]
  );
  const selectedRollbackTarget = useMemo(() => {
    if (!rollbackDeploymentRecord) {
      return null;
    }

    return resolveDeploymentTargetForRecord(rollbackDeploymentRecord, targets);
  }, [rollbackDeploymentRecord, targets]);
  const selectedRestartTarget = useMemo(() => {
    if (!restartDeploymentRecord) {
      return null;
    }

    return resolveDeploymentTargetForRecord(restartDeploymentRecord, targets);
  }, [restartDeploymentRecord, targets]);

  const selectedRepositoryId = useMemo(() => {
    const repository = asNamedEntity(selectedDeploymentTarget?.repository);
    return repository?._id || '';
  }, [selectedDeploymentTarget]);

  const selectedRepositoryReleaseOptions = selectedRepositoryId ? releaseOptionsByRepository[selectedRepositoryId] : undefined;
  const selectedRepositoryOverrideRecord = useMemo(
    () => repositories.find((repository) => repository._id === repositoryOverrideDialogRepositoryId) || null,
    [repositories, repositoryOverrideDialogRepositoryId]
  );
  const selectedRepositoryOverrideEntries = selectedRepositoryOverrideRecord?.deploymentOverrideSummary?.entries || [];
  const repositoryReadinessById = useMemo(
    () => new Map((rolloutMetadataReadiness?.repositories || []).filter((entry) => entry.id).map((entry) => [entry.id, entry])),
    [rolloutMetadataReadiness]
  );
  const serverReadinessById = useMemo(
    () => new Map((rolloutMetadataReadiness?.servers || []).filter((entry) => entry.id).map((entry) => [entry.id, entry])),
    [rolloutMetadataReadiness]
  );
  const targetReadinessById = useMemo(
    () => new Map((rolloutMetadataReadiness?.targets || []).filter((entry) => entry.id).map((entry) => [entry.id, entry])),
    [rolloutMetadataReadiness]
  );
  const rolloutMetadataIssuePreview = useMemo(() => (
    [
      ...(rolloutMetadataReadiness?.repositories || []),
      ...(rolloutMetadataReadiness?.servers || []),
      ...(rolloutMetadataReadiness?.targets || []),
    ]
      .flatMap((entry) => entry.issues.map((issue) => ({
        owner: entry.name,
        ...issue,
      })))
      .sort((left, right) => {
        if (left.severity === right.severity) {
          return 0;
        }

        return left.severity === 'error' ? -1 : 1;
      })
      .slice(0, 8)
  ), [rolloutMetadataReadiness]);

  const releaseChoiceOptions = useMemo(() => {
    if (!selectedRepositoryReleaseOptions) {
      return [];
    }

    const options: Array<{ key: string; label: string; selection: DeploymentReleaseSelection }> = [];

    if (selectedRepositoryReleaseOptions.defaultBranchHead) {
      options.push({
        key: `branch:${selectedRepositoryReleaseOptions.defaultBranchHead.name}`,
        label: `Branch head • ${selectedRepositoryReleaseOptions.defaultBranchHead.name} • ${selectedRepositoryReleaseOptions.defaultBranchHead.commitSha?.slice(0, 8)}`,
        selection: {
          sourceType: 'branch',
          ref: selectedRepositoryReleaseOptions.defaultBranchHead.name,
          commitSha: selectedRepositoryReleaseOptions.defaultBranchHead.commitSha,
          displayVersion: `${selectedRepositoryReleaseOptions.defaultBranchHead.name}@${selectedRepositoryReleaseOptions.defaultBranchHead.commitSha?.slice(0, 8)}`,
        },
      });
    }

    selectedRepositoryReleaseOptions.tags.forEach((tag) => {
      options.push({
        key: `tag:${tag.name}`,
        label: `Git tag • ${tag.name} • ${tag.commitSha?.slice(0, 8)}`,
        selection: {
          sourceType: 'tag',
          ref: tag.name,
          tagName: tag.name,
          commitSha: tag.commitSha,
          displayVersion: tag.name,
        },
      });
    });

    selectedRepositoryReleaseOptions.packages.forEach((pkg) => {
      options.push({
        key: `package:${pkg.version}`,
        label: `Package • ${pkg.version} • ${pkg.name}`,
        selection: {
          sourceType: 'package',
          ref: selectedRepositoryReleaseOptions.project.defaultBranch,
          packageVersion: pkg.version,
          displayVersion: pkg.version,
        },
      });
    });

    selectedRepositoryReleaseOptions.containerImages.forEach((repository) => {
      repository.tags.forEach((tag) => {
        options.push({
          key: `image:${repository.id}:${tag.name}`,
          label: `Image • ${tag.name} • ${repository.path || repository.name || 'container'}`,
          selection: {
            sourceType: 'image',
            ref: selectedRepositoryReleaseOptions.project.defaultBranch,
            imageTag: tag.name,
            displayVersion: tag.name,
          },
        });
      });
    });

    return options;
  }, [selectedRepositoryReleaseOptions]);

  useEffect(() => {
    if (!selectedRepositoryId || releaseOptionsByRepository[selectedRepositoryId]) {
      return;
    }

    deploymentDashboardApi.getRepositoryReleaseOptions(selectedRepositoryId)
      .then((response) => {
        setReleaseOptionsByRepository((current) => ({
          ...current,
          [selectedRepositoryId]: response.data.releaseOptions,
        }));
        setRepositories((current) => current.map((repository) => (
          repository._id === response.data.repository._id ? response.data.repository : repository
        )));
      })
      .catch(() => undefined);
  }, [releaseOptionsByRepository, selectedRepositoryId]);

  const handleMutation = async (callback: () => Promise<void>) => {
    setSaving(true);
    setError('');
    let actionError = '';
    try {
      await callback();
    } catch (err: unknown) {
      actionError = getRequestFailureMessage(
        err,
        'Failed to save changes',
        'The request timed out. The server may still be processing it, so the outcome is unknown. Check the latest status before trying again.'
      );
    } finally {
      // A failed response can still leave a new run on the server. Read its status, never resubmit the action.
      await loadAll();
      setDeploymentExplorerRefreshKey((current) => current + 1);
      if (actionError) {
        setError(actionError);
      }
      setSaving(false);
    }
  };

  const resetDeployDialog = (targetId = '') => {
    const target = targets.find((item) => item._id === targetId) || null;
    setDeployForm({
      ...initialDeployForm,
      deploymentTargetId: targetId,
      deploymentMode: resolveDefaultDeploymentMode(target),
      runtimeConfigTemplateId: '',
      runtimeConfigInstanceId: '',
    });
  };

  const openDeployDialog = (targetId = '') => {
    setError('');
    resetDeployDialog(targetId);
    setDeployDialogOpen(true);
  };

  const cacheDeploymentRecords = useCallback((records: DeploymentRecord[]) => {
    if (records.length === 0) {
      return;
    }

    setDeployments((current) => {
      const nextById = new Map(current.map((record) => [record._id, record]));
      records.forEach((record) => {
        nextById.set(record._id, record);
      });

      return Array.from(nextById.values()).sort((left, right) => {
        const leftTime = new Date(left.lastStatusAt || left.updatedAt || left.createdAt || 0).getTime();
        const rightTime = new Date(right.lastStatusAt || right.updatedAt || right.createdAt || 0).getTime();
        return rightTime - leftTime;
      });
    });
  }, []);

  const upsertDeploymentRecord = useCallback((record: DeploymentRecord) => {
    cacheDeploymentRecords([record]);
  }, [cacheDeploymentRecords]);

  const refreshDeploymentDetailsById = useCallback(async (deploymentId: string) => {
    const response = await deploymentDashboardApi.refreshDeployment(deploymentId);
    upsertDeploymentRecord(response.data);
    return response.data;
  }, [upsertDeploymentRecord]);

  const handleOpenDeploymentDetails = useCallback(async (deploymentId: string) => {
    const requestId = deploymentDetailsRequestRef.current + 1;
    deploymentDetailsRequestRef.current = requestId;
    setSelectedDeploymentId(deploymentId);
    setDeploymentDetailsLoading(true);
    setError('');

    try {
      await refreshDeploymentDetailsById(deploymentId);
    } catch (err: unknown) {
      if (requestId === deploymentDetailsRequestRef.current) {
        setError(getRequestFailureMessage(err, 'Failed to load deployment details'));
      }
    } finally {
      if (requestId === deploymentDetailsRequestRef.current) {
        setDeploymentDetailsLoading(false);
      }
    }
  }, [refreshDeploymentDetailsById]);

  const handleRefreshDeploymentDetails = useCallback(async () => {
    if (!selectedDeploymentId) {
      return;
    }

    const requestId = deploymentDetailsRequestRef.current + 1;
    deploymentDetailsRequestRef.current = requestId;
    setDeploymentDetailsLoading(true);
    setError('');

    try {
      await refreshDeploymentDetailsById(selectedDeploymentId);
    } catch (err: unknown) {
      if (requestId === deploymentDetailsRequestRef.current) {
        setError(getRequestFailureMessage(err, 'Failed to refresh deployment details'));
      }
    } finally {
      if (requestId === deploymentDetailsRequestRef.current) {
        setDeploymentDetailsLoading(false);
      }
    }
  }, [refreshDeploymentDetailsById, selectedDeploymentId]);

  const handleRefreshDeploymentRecord = (deploymentId: string) => {
    void handleMutation(async () => {
      const response = await deploymentDashboardApi.refreshDeployment(deploymentId);
      upsertDeploymentRecord(response.data);
    });
  };

  const handleComposeStartDeployment = (deploymentId: string) => {
    void handleMutation(async () => {
      await deploymentDashboardApi.composeStartDeployment(deploymentId);
    });
  };

  const handleComposeStopDeployment = (deploymentRecord: DeploymentRecord) => {
    if (!window.confirm('Run docker compose down on the target host through GitLab? This stops the compose project.')) {
      return;
    }

    void handleMutation(async () => {
      await deploymentDashboardApi.composeStopDeployment(deploymentRecord._id);
    });
  };

  const handleOpenRestartDeployment = (deploymentRecord: DeploymentRecord) => {
    setError('');
    setRestartDeploymentRecord(deploymentRecord);
    setRestartReason('');
    setRestartDialogOpen(true);
  };

  const handleOpenRollbackDeployment = (deploymentRecord: DeploymentRecord) => {
    setError('');
    const rollbackTarget = resolveDeploymentTargetForRecord(deploymentRecord, targets);
    setRollbackDeploymentRecord(deploymentRecord);
    setRollbackDeploymentMode(
      resolveRollbackMode(deploymentRecord.deploymentMode, rollbackTarget)
    );
    setRollbackReason('');
    setRollbackDialogOpen(true);
  };

  const getCustomerName = (customer: string | DeploymentCustomer | null | undefined) => (
    asNamedEntity(customer)?.name || '-'
  );

  const getServerName = (server: string | DeploymentServer | null | undefined) => (
    asNamedEntity(server)?.name || '-'
  );

  const getRepositoryName = (repository: string | RepositoryCatalogRecord | null | undefined) => (
    asNamedEntity(repository)?.pathWithNamespace || asNamedEntity(repository)?.name || '-'
  );

  const selectedDeployment = useMemo(
    () => deployments.find((deploymentRecord) => deploymentRecord._id === selectedDeploymentId) || null,
    [deployments, selectedDeploymentId]
  );
  const selectedDeploymentFailureClass = selectedDeployment
    ? getDeploymentFailureClass(selectedDeployment)
    : '';
  const selectedDeploymentFailureMessage = selectedDeployment
    ? getDeploymentFailureMessage(selectedDeployment)
    : '';
  const selectedDeploymentProgressSummaryLines = selectedDeployment
    ? getDeploymentProgressSummaryLines(selectedDeployment)
    : [];

  const formatScopedReference = (
    customer: string | DeploymentCustomer | null | undefined,
    repository: string | RepositoryCatalogRecord | null | undefined,
    server: string | DeploymentServer | null | undefined,
    environment?: string
  ) => {
    const segments = [
      asNamedEntity(customer)?.slug || 'global',
      asNamedEntity(repository)?.pathWithNamespace || 'all-repos',
      asNamedEntity(server)?.slug || 'all-servers',
      environment || 'all-envs',
    ];

    return segments.join(' / ');
  };

  const handleDelete = async (
    label: string,
    action: () => Promise<void>
  ) => {
    if (!window.confirm(`Delete ${label}? This action cannot be undone.`)) {
      return;
    }

    await handleMutation(action);
  };

  const handleCustomerSubmit = async () => {
    const payload = {
      name: customerForm.name,
      slug: customerForm.slug,
      description: customerForm.description,
      status: customerForm.status,
      tags: customerForm.tags,
    };

    await handleMutation(async () => {
      if (customerForm.id) {
        await deploymentDashboardApi.updateCustomer(customerForm.id, payload);
      } else {
        await deploymentDashboardApi.createCustomer(payload);
      }
      setCustomerDialogOpen(false);
      setCustomerForm(initialCustomerForm);
    });
  };

  const handleSecretSubmit = async () => {
    const payload = {
      name: secretForm.name,
      description: secretForm.description,
      gcpProjectId: secretForm.gcpProjectId,
      secretId: secretForm.secretId,
      secretVersion: secretForm.secretVersion,
      referenceKey: secretForm.referenceKey,
      secretType: secretForm.secretType,
      customer: secretForm.customer || null,
      repository: secretForm.repository || null,
      server: secretForm.server || null,
      environment: secretForm.environment,
      allowGlobalFallback: secretForm.allowHostFileMaterialization
        ? false
        : secretForm.allowGlobalFallback,
      ...(isAdmin
        ? { allowHostFileMaterialization: secretForm.allowHostFileMaterialization }
        : {}),
      labels: secretForm.labels,
      isActive: secretForm.isActive,
    };

    await handleMutation(async () => {
      if (
        secretForm.allowHostFileMaterialization
        && (!secretForm.customer || !secretForm.repository || !secretForm.server || !secretForm.environment.trim())
      ) {
        throw new Error(
          'Managed host-file references require exact customer, repository, server, and environment scope.'
        );
      }
      if (secretForm.id) {
        await deploymentDashboardApi.updateSecretReference(secretForm.id, payload);
      } else {
        await deploymentDashboardApi.createSecretReference(payload);
      }
      setSecretDialogOpen(false);
      setSecretForm(initialSecretForm);
    });
  };

  const handleParameterSubmit = async () => {
    const payload = {
      name: parameterForm.name,
      description: parameterForm.description,
      gcpProjectId: parameterForm.gcpProjectId,
      location: parameterForm.location,
      parameterId: parameterForm.parameterId,
      parameterVersion: parameterForm.parameterVersion,
      referenceKey: parameterForm.referenceKey,
      parameterFormat: parameterForm.parameterFormat,
      customer: parameterForm.customer || null,
      repository: parameterForm.repository || null,
      server: parameterForm.server || null,
      environment: parameterForm.environment,
      allowGlobalFallback: parameterForm.allowGlobalFallback,
      labels: parameterForm.labels,
      isActive: parameterForm.isActive,
    };

    await handleMutation(async () => {
      if (parameterForm.id) {
        await deploymentDashboardApi.updateParameterReference(parameterForm.id, payload);
      } else {
        await deploymentDashboardApi.createParameterReference(payload);
      }
      setParameterDialogOpen(false);
      setParameterForm(initialParameterForm);
    });
  };

  const handleServerSubmit = async () => {
    const payload = {
      customer: serverForm.customer,
      name: serverForm.name,
      slug: serverForm.slug,
      hostname: serverForm.hostname,
      ipAddress: serverForm.ipAddress,
      sshPort: Number(serverForm.sshPort || 22),
      description: serverForm.description,
      environments: serverForm.environments,
      tags: serverForm.tags,
      secretReferences: {
        sshUser: serverForm.sshUser || null,
        sshPrivateKey: serverForm.sshPrivateKey || null,
        sshKnownHosts: serverForm.sshKnownHosts || null,
        sshPassword: serverForm.sshPassword || null,
      },
      runtime: {
        deployPath: serverForm.deployPath,
        composeFilePath: serverForm.composeFilePath,
        composeEnvFile: serverForm.composeEnvFile,
        composeNetworkName: serverForm.composeNetworkName,
        dockerServiceName: serverForm.dockerServiceName,
        runtimeEnvFilePath: serverForm.runtimeEnvFilePath,
        remoteServiceRestartCommand: serverForm.remoteServiceRestartCommand,
        healthcheckUrl: serverForm.healthcheckUrl,
      },
      bootstrap: {
        privilegeModel: serverForm.privilegeModel,
        osFamily: serverForm.osFamily,
        packageManager: serverForm.packageManager,
        nginxManaged: serverForm.nginxManaged,
        tlsEnabled: serverForm.tlsEnabled,
        supportedModes: serverForm.supportedModes,
        gcpCredentialsBasePath: serverForm.gcpCredentialsBasePath,
        allowedCredentialCopyTargetIds: serverForm.allowedCredentialCopyTargetIds,
      },
    };

    await handleMutation(async () => {
      if (serverForm.id) {
        await deploymentDashboardApi.updateServer(serverForm.id, payload);
      } else {
        await deploymentDashboardApi.createServer(payload);
      }
      setServerDialogOpen(false);
      setServerForm(initialServerForm);
    });
  };

  const handleTemplateSubmit = async () => {
    if (templateVariablesValidation.error) {
      setError('');
      return;
    }

    const payload = {
      name: templateForm.name,
      slug: templateForm.slug,
      description: templateForm.description,
      customer: templateForm.customer || null,
      server: templateForm.server || null,
      repository: templateForm.repository || null,
      environment: templateForm.environment,
      tags: templateForm.tags,
      variables: templateVariablesValidation.parsedValue ?? [],
      isActive: templateForm.isActive,
    };

    await handleMutation(async () => {
      if (templateForm.id) {
        await deploymentDashboardApi.updateRuntimeConfigTemplate(templateForm.id, payload);
      } else {
        await deploymentDashboardApi.createRuntimeConfigTemplate(payload);
      }
      setTemplateDialogOpen(false);
      setTemplateForm(initialTemplateForm);
    });
  };

  const handleInstanceSubmit = async () => {
    if (instanceOverridesValidation.error) {
      setError('');
      return;
    }

    const payload = {
      name: instanceForm.name,
      template: instanceForm.template,
      customer: instanceForm.customer || null,
      server: instanceForm.server || null,
      repository: instanceForm.repository || null,
      deploymentTarget: instanceForm.deploymentTarget || null,
      environment: instanceForm.environment,
      overrides: instanceOverridesValidation.parsedValue ?? [],
      isActive: instanceForm.isActive,
    };

    await handleMutation(async () => {
      if (instanceForm.id) {
        await deploymentDashboardApi.updateRuntimeConfigInstance(instanceForm.id, payload);
      } else {
        await deploymentDashboardApi.createRuntimeConfigInstance(payload);
      }
      setInstanceDialogOpen(false);
      setInstanceForm(initialInstanceForm);
    });
  };

  const handleTargetSubmit = async () => {
    if (targetPipelineVariablesValidation.error || targetModeJobMappingValidation.error) {
      setError('');
      return;
    }
    const nginxUpstreamPort = targetForm.setupNginxUpstreamPort.trim();
    if (
      isAdmin && nginxUpstreamPort
      && (!/^\d+$/.test(nginxUpstreamPort)
        || Number(nginxUpstreamPort) < 1
        || Number(nginxUpstreamPort) > 65535)
    ) {
      setError('Nginx upstream port must be an integer between 1 and 65535.');
      return;
    }

    const payload = {
      name: targetForm.name,
      slug: targetForm.slug,
      customer: targetForm.customer,
      server: targetForm.server,
      repository: targetForm.repository,
      environment: targetForm.environment,
      deploymentStrategy: targetForm.deploymentStrategy,
      deployJobName: targetForm.deployJobName,
      setupJobName: targetForm.setupJobName,
      ...(isAdmin ? { setupNginxUpstreamPort: nginxUpstreamPort || null } : {}),
      setupFamily: targetForm.setupFamily,
      allowedDeploymentModes: targetForm.allowedDeploymentModes,
      defaultDeploymentMode: targetForm.defaultDeploymentMode,
      modeJobMapping: targetModeJobMappingValidation.parsedValue ?? [],
      gcpSetupProfile: {
        scope: targetForm.gcpProfileScope,
        appCode: targetForm.gcpAppCode,
        customerCode: targetForm.gcpCustomerCode,
        robotCode: targetForm.gcpRobotCode,
        configParameterRole: targetForm.configParameterRole,
        credentialsHostPath: targetForm.credentialsHostPath,
        catalogPath: targetForm.catalogPath,
        publicConfigParameterId: targetForm.publicConfigParameterId,
      },
      versionStrategy: targetForm.versionStrategy,
      defaultConfigTemplate: targetForm.defaultConfigTemplate || null,
      defaultConfigInstance: targetForm.defaultConfigInstance || null,
      stackBundleKey: targetForm.stackBundleKey,
      stackPhase: targetForm.stackPhase,
      dependencyRepositoryKeys: targetForm.dependencyRepositoryKeys,
      healthcheckProfile: targetForm.healthcheckProfile,
      healthcheckUrl: targetForm.healthcheckUrl,
      enabled: targetForm.enabled,
      tags: targetForm.tags,
      remoteSettings: {
        deployPath: targetForm.deployPath,
        composeFilePath: targetForm.composeFilePath,
        composeEnvFile: targetForm.composeEnvFile,
        composeNetworkName: targetForm.composeNetworkName,
        dockerServiceName: targetForm.dockerServiceName,
        runtimeEnvFilePath: targetForm.runtimeEnvFilePath,
        remoteServiceRestartCommand: targetForm.remoteServiceRestartCommand,
      },
      metadata: {
        setupDomain: targetForm.setupDomain,
        setupAppUser: targetForm.setupAppUser,
        setupPublicPort: targetForm.setupPublicPort,
        setupPrivatePort: targetForm.setupPrivatePort,
        setupTlsEnabled: targetForm.setupTlsEnabled,
        setupNginxSiteName: targetForm.setupNginxSiteName,
        acmeEmail: targetForm.acmeEmail,
      },
      pipelineVariables: targetPipelineVariablesValidation.parsedValue ?? {},
    };

    await handleMutation(async () => {
      if (targetForm.id) {
        await deploymentDashboardApi.updateDeploymentTarget(targetForm.id, payload);
      } else {
        await deploymentDashboardApi.createDeploymentTarget(payload);
      }
      setTargetDialogOpen(false);
      setTargetForm(initialTargetForm);
    });
  };

  const handleRepositorySubmit = async () => {
    const payload = repositoryForm.gitlabProjectId
      ? { gitlabProjectId: Number(repositoryForm.gitlabProjectId) }
      : { pathWithNamespace: repositoryForm.pathWithNamespace.trim() };

    await handleMutation(async () => {
      await deploymentDashboardApi.createRepository(payload);
      setRepositoryDialogOpen(false);
      setRepositoryForm(initialRepositoryForm);
    });
  };

  const openRepositoryOverrideDialog = (repositoryId: string) => {
    setRepositoryOverrideDialogRepositoryId(repositoryId);
    setRepositoryOverrideReason(DEFAULT_REPOSITORY_OVERRIDE_REASON);
    setError('');
  };

  const closeRepositoryOverrideDialog = () => {
    setRepositoryOverrideDialogRepositoryId('');
    setRepositoryOverrideReason(DEFAULT_REPOSITORY_OVERRIDE_REASON);
  };

  const handleClearRepositoryOverride = async (field: string) => {
    if (!selectedRepositoryOverrideRecord) {
      return;
    }

    if (!isRepositoryOverrideField(field)) {
      setError(`Unsupported repository override field "${field}"`);
      return;
    }

    await handleMutation(async () => {
      const response = await deploymentDashboardApi.updateRepository(selectedRepositoryOverrideRecord._id, {
        deploymentProfile: {
          [field]: getRepositoryOverrideClearValue(field),
        },
        deploymentProfileOverrideReason: repositoryOverrideReason.trim(),
      });

      setSetupNotification({
        message: `Cleared ${field} override for ${selectedRepositoryOverrideRecord.pathWithNamespace}.`,
        severity: 'success',
      });

      if (!response.data.deploymentOverrideSummary?.active) {
        closeRepositoryOverrideDialog();
      }
    });
  };

  const handleClearAllRepositoryOverrides = async () => {
    if (!selectedRepositoryOverrideRecord) {
      return;
    }

    const deploymentProfile = selectedRepositoryOverrideEntries.reduce<Partial<Record<RepositoryOverrideField, unknown>>>(
      (accumulator, entry) => {
        if (isRepositoryOverrideField(entry.field)) {
          accumulator[entry.field] = getRepositoryOverrideClearValue(entry.field);
        }

        return accumulator;
      },
      {}
    );

    if (Object.keys(deploymentProfile).length === 0) {
      closeRepositoryOverrideDialog();
      return;
    }

    await handleMutation(async () => {
      const response = await deploymentDashboardApi.updateRepository(selectedRepositoryOverrideRecord._id, {
        deploymentProfile,
        deploymentProfileOverrideReason: repositoryOverrideReason.trim(),
      });

      setSetupNotification({
        message: `Cleared all active repo overrides for ${selectedRepositoryOverrideRecord.pathWithNamespace}.`,
        severity: 'success',
      });

      if (!response.data.deploymentOverrideSummary?.active) {
        closeRepositoryOverrideDialog();
      }
    });
  };

  const handleDeploySubmit = async () => {
    const releaseOption = releaseChoiceOptions.find((option) => option.key === deployForm.sourceKey);
    if (!releaseOption) {
      setError('Please select a version, tag, package, image, or branch snapshot to deploy');
      return;
    }

    await handleMutation(async () => {
      await deploymentDashboardApi.createDeployment({
        deploymentTargetId: deployForm.deploymentTargetId,
        releaseSelection: releaseOption.selection,
        runtimeConfigTemplateId: deployForm.runtimeConfigTemplateId || null,
        runtimeConfigInstanceId: deployForm.runtimeConfigInstanceId || null,
        deploymentMode: deployForm.deploymentMode || undefined,
        reason: deployForm.reason,
      });
      setDeployDialogOpen(false);
      resetDeployDialog();
    });
  };

  const handleRestartSubmit = async () => {
    if (!restartDeploymentRecord) {
      return;
    }

    await handleMutation(async () => {
      const response = await deploymentDashboardApi.restartDeployment(
        restartDeploymentRecord._id,
        restartReason || undefined
      );
      if (selectedDeploymentId === restartDeploymentRecord._id) {
        setSelectedDeploymentId(response.data._id);
      }
      setRestartDialogOpen(false);
      setRestartDeploymentRecord(null);
      setRestartReason('');
    });
  };

  const closeRestartDialog = () => {
    setRestartDialogOpen(false);
    setRestartDeploymentRecord(null);
    setRestartReason('');
  };

  const handleRollbackSubmit = async () => {
    if (!rollbackDeploymentRecord) {
      return;
    }

    await handleMutation(async () => {
      await deploymentDashboardApi.rollbackDeployment(
        rollbackDeploymentRecord._id,
        rollbackReason,
        rollbackDeploymentMode || undefined
      );
      setRollbackDialogOpen(false);
      setRollbackDeploymentRecord(null);
      setRollbackDeploymentMode('');
      setRollbackReason('');
    });
  };

  const closeRollbackDialog = () => {
    setRollbackDialogOpen(false);
    setRollbackDeploymentRecord(null);
    setRollbackDeploymentMode('');
    setRollbackReason('');
  };

  const renderStatCard = (title: string, value: number, IconComponent: LucideIcon) => (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 52,
              height: 52,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '16px',
              background: 'rgba(124, 58, 237, 0.14)',
              color: 'primary.main',
            }}
          >
            {renderIcon(IconComponent, 22)}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', zIndex: 1 }}>
      <Box sx={{ maxWidth: 1600, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={3} sx={{ mb: 4 }}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Chip label="Deployment Control Center" color="primary" variant="outlined" />
              <Chip label="GitLab-driven" color="info" variant="outlined" />
              <Chip label="Rollback Ready" color="warning" variant="outlined" />
            </Stack>
            <Typography variant="h3" fontWeight={700} sx={{ color: 'text.primary' }}>
              Deployment and Configuration Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 880 }}>
              Manage customers, servers, GitLab repositories, runtime configuration profiles, deployment targets,
              and audited deploy or rollback actions from one place.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button variant="outlined" startIcon={renderIcon(ArrowLeft)} onClick={onBack}>
              Back
            </Button>
            <Button variant="outlined" startIcon={renderIcon(RefreshCcw)} onClick={loadAll}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={renderIcon(Rocket)} onClick={() => openDeployDialog()}>
              Launch Deployment
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ pb: 1 }}>
            <Tabs
              value={activeTab}
              onChange={(_event, value: DashboardTab) => setActiveTab(value)}
              variant="scrollable"
              allowScrollButtonsMobile
            >
              <Tab value="overview" label="Overview" />
              <Tab value="customers" label="Customers" />
              <Tab value="servers" label="Servers" />
              <Tab value="repositories" label="Repositories" />
              <Tab value="targets" label="Targets" />
              <Tab value="configs" label="Configs" />
              <Tab value="setup" label="Server Setup" />
              <Tab value="deployments" label="Deployments" />
            </Tabs>
          </CardContent>
        </Card>

        {activeTab === 'overview' && (
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} lg={2}>{renderStatCard('Customers', overview?.customers || 0, Users)}</Grid>
              <Grid item xs={12} sm={6} lg={2}>{renderStatCard('Servers', overview?.servers || 0, Server)}</Grid>
              <Grid item xs={12} sm={6} lg={2}>{renderStatCard('Repositories', overview?.repositories || 0, FolderGit2)}</Grid>
              <Grid item xs={12} sm={6} lg={2}>{renderStatCard('Targets', overview?.targets || 0, Tags)}</Grid>
              <Grid item xs={12} sm={6} lg={2}>{renderStatCard('Config Profiles', overview?.configs || 0, ShieldCheck)}</Grid>
              <Grid item xs={12} sm={6} lg={2}>{renderStatCard('Deployments', overview?.deployments || 0, CloudUpload)}</Grid>
            </Grid>

            <Card>
              <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      Rollout Metadata Readiness
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Verify repository profiles, server bootstrap metadata, and target setup metadata before launching setup.
                    </Typography>
                  </Box>
                  <Button variant="outlined" startIcon={renderIcon(RefreshCcw)} onClick={loadAll}>
                    Refresh Readiness
                  </Button>
                </Stack>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="overline" color="text.secondary">Blocking Issues</Typography>
                        <Typography variant="h4" fontWeight={700}>{rolloutMetadataReadiness?.summary.blockingIssueCount || 0}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="overline" color="text.secondary">Warnings</Typography>
                        <Typography variant="h4" fontWeight={700}>{rolloutMetadataReadiness?.summary.warningCount || 0}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="overline" color="text.secondary">Ready Targets</Typography>
                        <Typography variant="h4" fontWeight={700}>{rolloutMetadataReadiness?.summary.targets.ready || 0}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="overline" color="text.secondary">In-Scope Repos</Typography>
                        <Typography variant="h4" fontWeight={700}>{rolloutMetadataReadiness?.summary.inScopeRepositoryCount || 0}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Alert severity={metadataReadinessColor(rolloutMetadataIssuePreview)}>
                  {(rolloutMetadataReadiness?.summary.blockingIssueCount || 0) > 0
                    ? 'Setup launches are blocked until the rollout metadata errors are fixed.'
                    : (rolloutMetadataReadiness?.summary.warningCount || 0) > 0
                      ? 'Rollout metadata is usable, but there are warnings worth fixing before wider rollout.'
                      : 'Repository, server, and target metadata checks are all passing.'}
                </Alert>

                {rolloutMetadataIssuePreview.length > 0 && (
                  <TableContainer sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Severity</TableCell>
                          <TableCell>Owner</TableCell>
                          <TableCell>Field</TableCell>
                          <TableCell>Issue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rolloutMetadataIssuePreview.map((issue, index) => (
                          <TableRow key={`${issue.owner}-${issue.field}-${index}`}>
                            <TableCell>
                              <Chip size="small" color={issue.severity === 'error' ? 'error' : 'warning'} label={issue.severity} />
                            </TableCell>
                            <TableCell>{issue.owner}</TableCell>
                            <TableCell>{issue.field}</TableCell>
                            <TableCell>{issue.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                  Recent Activity
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Repository</TableCell>
                        <TableCell>Environment</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Version</TableCell>
                        <TableCell>Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(overview?.recentDeployments || []).map((deploymentRecord) => (
                        <TableRow key={deploymentRecord._id}>
                          <TableCell>{getRepositoryName(deploymentRecord.repository)}</TableCell>
                          <TableCell>{deploymentRecord.environment}</TableCell>
                          <TableCell>{deploymentRecord.actionType}</TableCell>
                          <TableCell>
                            <Chip size="small" color={statusColor(deploymentRecord.status)} label={deploymentRecord.status} />
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography variant="body2">{deploymentRecord.requestedRelease?.displayVersion || '-'}</Typography>
                              {deploymentRecord.deploymentMode && (
                                <Typography variant="caption" color="text.secondary">
                                  {formatDeploymentModeLabel(deploymentRecord.deploymentMode)}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>{formatDate(deploymentRecord.lastStatusAt || deploymentRecord.updatedAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Stack>
        )}

        {activeTab === 'customers' && (
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h5" fontWeight={700}>Customers</Typography>
                  <Typography variant="body2" color="text.secondary">Group target servers and deployments by customer.</Typography>
                </Box>
                <Button variant="contained" startIcon={renderIcon(Plus)} onClick={() => { setCustomerForm(initialCustomerForm); setCustomerDialogOpen(true); }}>
                  Add Customer
                </Button>
              </Stack>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Slug</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Tags</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow key={customer._id}>
                        <TableCell>{customer.name}</TableCell>
                        <TableCell>{customer.slug}</TableCell>
                        <TableCell><Chip size="small" label={customer.status} color={customer.status === 'active' ? 'success' : 'default'} /></TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {customer.tags.map((tag) => <Chip key={tag} size="small" label={tag} variant="outlined" />)}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" justifyContent="flex-end" spacing={1}>
                            <IconButton onClick={() => { setCustomerForm({ id: customer._id, name: customer.name, slug: customer.slug, description: customer.description || '', status: customer.status, tags: customer.tags.join(', ') }); setCustomerDialogOpen(true); }}>
                              {renderIcon(Save)}
                            </IconButton>
                            <IconButton color="error" onClick={() => handleDelete(`customer ${customer.name}`, () => deploymentDashboardApi.deleteCustomer(customer._id).then(() => undefined))}>
                              {renderIcon(Trash2)}
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {activeTab === 'servers' && (
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h5" fontWeight={700}>Servers</Typography>
                  <Typography variant="body2" color="text.secondary">Track deploy hosts, SSH secret references, and runtime defaults.</Typography>
                </Box>
                <Button variant="contained" startIcon={renderIcon(Plus)} onClick={() => { setServerForm(initialServerForm); setServerDialogOpen(true); }}>
                  Add Server
                </Button>
              </Stack>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Host</TableCell>
                      <TableCell>Environments</TableCell>
                      <TableCell>Secret Refs</TableCell>
                      <TableCell>Setup Readiness</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {servers.map((server) => (
                      <TableRow key={server._id}>
                        <TableCell>{server.name}</TableCell>
                        <TableCell>{getCustomerName(server.customer)}</TableCell>
                        <TableCell>{server.hostname || server.ipAddress}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {server.environments.map((environment) => <Chip key={environment} size="small" label={environment} variant="outlined" />)}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {[server.secretReferences.sshUser, server.secretReferences.sshPrivateKey, server.secretReferences.sshKnownHosts]
                              .map((item) => asNamedEntity(item)?.name)
                              .filter(Boolean)
                              .join(', ') || 'No SSH refs configured'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.75}>
                            <Chip
                              size="small"
                              color={metadataReadinessColor(serverReadinessById.get(server._id)?.issues || [])}
                              label={serverReadinessById.get(server._id)?.ready === false ? 'Needs attention' : 'Ready'}
                            />
                            {serverReadinessById.get(server._id)?.issues?.[0] && (
                              <Typography variant="caption" color="text.secondary">
                                {serverReadinessById.get(server._id)?.issues[0].message}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" justifyContent="flex-end" spacing={1}>
                            <IconButton onClick={() => {
                              setServerForm({
                                id: server._id,
                                customer: asNamedEntity(server.customer)?._id || '',
                                name: server.name,
                                slug: server.slug,
                                hostname: server.hostname || '',
                                ipAddress: server.ipAddress,
                                sshPort: String(server.sshPort || 22),
                                description: server.description || '',
                                environments: server.environments.join(', '),
                                tags: server.tags.join(', '),
                                sshUser: asNamedEntity(server.secretReferences.sshUser)?._id || '',
                                sshPrivateKey: asNamedEntity(server.secretReferences.sshPrivateKey)?._id || '',
                                sshKnownHosts: asNamedEntity(server.secretReferences.sshKnownHosts)?._id || '',
                                sshPassword: asNamedEntity(server.secretReferences.sshPassword)?._id || '',
                                deployPath: server.runtime?.deployPath || '',
                                composeFilePath: server.runtime?.composeFilePath || '',
                                composeEnvFile: server.runtime?.composeEnvFile || '',
                                composeNetworkName: server.runtime?.composeNetworkName || '',
                                dockerServiceName: server.runtime?.dockerServiceName || '',
                                runtimeEnvFilePath: server.runtime?.runtimeEnvFilePath || '',
                                remoteServiceRestartCommand: server.runtime?.remoteServiceRestartCommand || '',
                                healthcheckUrl: server.runtime?.healthcheckUrl || '',
                                privilegeModel: server.bootstrap?.privilegeModel || 'unknown',
                                osFamily: server.bootstrap?.osFamily || 'unknown',
                                packageManager: server.bootstrap?.packageManager || 'unknown',
                                nginxManaged: server.bootstrap?.nginxManaged || false,
                                tlsEnabled: server.bootstrap?.tlsEnabled || false,
                                supportedModes: server.bootstrap?.supportedModes?.join(', ') || '',
                                gcpCredentialsBasePath: server.bootstrap?.gcpCredentialsBasePath || '',
                                allowedCredentialCopyTargetIds: server.bootstrap?.allowedCredentialCopyTargetIds?.join(', ') || '',
                              });
                              setServerDialogOpen(true);
                            }}>
                              {renderIcon(Save)}
                            </IconButton>
                            <IconButton color="error" onClick={() => handleDelete(`server ${server.name}`, () => deploymentDashboardApi.deleteServer(server._id).then(() => undefined))}>
                              {renderIcon(Trash2)}
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {activeTab === 'repositories' && (
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>Repositories</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sync the GitLab project catalog, inspect tags, package versions, image tags, and commit heads.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button variant="outlined" startIcon={renderIcon(RefreshCcw)} onClick={() => handleMutation(async () => { await deploymentDashboardApi.syncRepositories(); })}>
                      Sync Scope
                    </Button>
                    <Button variant="contained" startIcon={renderIcon(Plus)} onClick={() => { setRepositoryForm(initialRepositoryForm); setRepositoryDialogOpen(true); }}>
                      Register Repo
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table
                    sx={{ tableLayout: 'fixed', minWidth: repositoryTableColumnWidths.reduce((a, b) => a + b, 0) }}
                  >
                    <colgroup>
                      {repositoryTableColumnWidths.map((width, index) => (
                        <col key={`repository-table-col-${index}`} style={{ width: `${width}px` }} />
                      ))}
                    </colgroup>
                    <TableHead>
                      <TableRow>
                        {REPOSITORY_TABLE_COLUMN_HEADERS.map((column, columnIndex) => (
                          <TableCell
                            key={column.label}
                            align={column.align}
                            sx={{
                              position: 'relative',
                              overflow: 'visible',
                              verticalAlign: 'middle',
                            }}
                          >
                            {column.label}
                            <Box
                              role="separator"
                              tabIndex={0}
                              aria-orientation="vertical"
                              aria-label={`Resize ${column.label} column`}
                              aria-valuemin={REPOSITORY_TABLE_MIN_COLUMN_WIDTH}
                              aria-valuemax={REPOSITORY_TABLE_MAX_COLUMN_WIDTH}
                              aria-valuenow={clampRepositoryTableColumnWidth(repositoryTableColumnWidths[columnIndex])}
                              onMouseDown={beginRepositoryTableColumnResize(columnIndex)}
                              onKeyDown={(keyboardEvent) => {
                                if (keyboardEvent.key === 'ArrowLeft') {
                                  keyboardEvent.preventDefault();
                                  nudgeRepositoryTableColumnWidth(columnIndex, -REPOSITORY_TABLE_COLUMN_KEYBOARD_STEP);
                                } else if (keyboardEvent.key === 'ArrowRight') {
                                  keyboardEvent.preventDefault();
                                  nudgeRepositoryTableColumnWidth(columnIndex, REPOSITORY_TABLE_COLUMN_KEYBOARD_STEP);
                                }
                              }}
                              sx={{
                                position: 'absolute',
                                top: 0,
                                right: -3,
                                width: 6,
                                height: '100%',
                                cursor: 'col-resize',
                                zIndex: 1,
                                touchAction: 'none',
                                '&:hover': { bgcolor: 'action.hover' },
                                '&:focus-visible': {
                                  outline: '2px solid',
                                  outlineColor: 'primary.main',
                                  outlineOffset: 2,
                                },
                              }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {repositories.map((repository) => {
                        const repositoryProfile = repository.resolvedDeploymentProfile || repository.deploymentProfile;
                        const repositoryReadiness = repositoryReadinessById.get(repository._id);
                        const manifestStatus = repository.deploymentManifest?.status || 'unknown';
                        const hasActiveOverrides = Boolean(repository.deploymentOverrideSummary?.active);

                        return (
                          <TableRow key={repository._id}>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                              <Stack spacing={0.5}>
                                <Typography variant="body1" fontWeight={600}>{repository.pathWithNamespace}</Typography>
                                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                  <Typography variant="caption" color="text.secondary">GitLab project #{repository.gitlabProjectId}</Typography>
                                  <Chip size="small" variant="outlined" label={`Manifest: ${manifestStatus}`} />
                                  {hasActiveOverrides && <Chip size="small" color="warning" label="Override active" />}
                                </Stack>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                              <Stack spacing={0.5}>
                                <Typography variant="body2">
                                  {repositoryProfile?.setupFamily || 'No setup family'}
                                  {repositoryProfile?.defaultDeploymentMode ? ` • ${formatDeploymentModeLabel(repositoryProfile.defaultDeploymentMode)}` : ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {(repositoryProfile?.allowedDeploymentModes || []).join(', ') || 'No deployment modes'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {repositoryProfile?.stackBundleKey
                                    ? `${repositoryProfile.stackBundleKey} • phase ${repositoryProfile.stackPhase ?? 0}`
                                    : 'No stack profile'}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}><Chip size="small" label={repository.artifactKind} /></TableCell>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-all' }}>{repository.versionSummary?.latestTag || '-'}</TableCell>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-all' }}>{repository.versionSummary?.latestPackageVersion || '-'}</TableCell>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-all' }}>{repository.versionSummary?.latestImageTag || '-'}</TableCell>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-all' }}>{repository.versionSummary?.latestCommitSha?.slice(0, 8) || '-'}</TableCell>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                              <Stack spacing={0.75}>
                                <Chip
                                  size="small"
                                  color={repositoryReadinessChipColor(repositoryReadiness)}
                                  label={repositoryReadinessChipLabel(repositoryReadiness)}
                                />
                                {repositoryReadiness?.issues?.[0] && (
                                  <Typography variant="caption" color="text.secondary">
                                    {repositoryReadiness.issues[0].message}
                                  </Typography>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                                {hasActiveOverrides && (
                                  <Tooltip title="Inspect and clear active overrides">
                                    <IconButton color="warning" onClick={() => openRepositoryOverrideDialog(repository._id)}>
                                      {renderIcon(Eye)}
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <IconButton onClick={() => deploymentDashboardApi.getRepositoryReleaseOptions(repository._id).then((response) => {
                                  setReleaseOptionsByRepository((current) => ({
                                    ...current,
                                    [repository._id]: response.data.releaseOptions,
                                  }));
                                  setRepositories((current) => current.map((item) => (
                                    item._id === response.data.repository._id ? response.data.repository : item
                                  )));
                                }).catch((err: unknown) => setError(getRequestFailureMessage(err, 'Failed to refresh repository versions')))}>
                                  {renderIcon(RefreshCcw)}
                                </IconButton>
                                <FormControlLabel
                                  control={(
                                    <Switch
                                      checked={repository.enabled}
                                      onChange={(_, checked) => handleMutation(async () => {
                                        await deploymentDashboardApi.updateRepository(repository._id, { enabled: checked });
                                      })}
                                    />
                                  )}
                                  label=""
                                />
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Stack>
        )}

        {activeTab === 'targets' && (
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h5" fontWeight={700}>Deployment Targets</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Map repositories to customer servers and environments, then attach default config profiles and remote runtime paths.
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={renderIcon(Plus)} onClick={() => { setTargetForm(initialTargetForm); setTargetDialogOpen(true); }}>
                  Add Target
                </Button>
              </Stack>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Target</TableCell>
                      <TableCell>Repository</TableCell>
                      <TableCell>Customer / Server</TableCell>
                      <TableCell>Environment</TableCell>
                      <TableCell>Setup Profile</TableCell>
                      <TableCell>Default Config</TableCell>
                      <TableCell>Readiness</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {targets.map((target) => (
                      <TableRow key={target._id}>
                        <TableCell>{target.name}</TableCell>
                        <TableCell>{getRepositoryName(target.repository)}</TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2">{getCustomerName(target.customer)}</Typography>
                            <Typography variant="caption" color="text.secondary">{getServerName(target.server)}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell><Chip size="small" label={target.environment} color="info" /></TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2">
                              {target.setupFamily || 'No setup family'}
                              {target.defaultDeploymentMode ? ` • ${formatDeploymentModeLabel(target.defaultDeploymentMode)}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(target.allowedDeploymentModes || []).join(', ') || 'No deployment modes'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {target.stackBundleKey
                                ? `${target.stackBundleKey} • phase ${target.stackPhase ?? 0}`
                                : 'No stack profile'}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="caption">{asNamedEntity(target.defaultConfigTemplate)?.name || 'No template'}</Typography>
                            <Typography variant="caption" color="text.secondary">{asNamedEntity(target.defaultConfigInstance)?.name || 'No instance'}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.75}>
                            <Chip
                              size="small"
                              color={metadataReadinessColor(targetReadinessById.get(target._id)?.issues || [])}
                              label={targetReadinessById.get(target._id)?.ready === false ? 'Needs attention' : 'Ready'}
                            />
                            {targetReadinessById.get(target._id)?.issues?.[0] && (
                              <Typography variant="caption" color="text.secondary">
                                {targetReadinessById.get(target._id)?.issues[0].message}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" justifyContent="flex-end" spacing={1}>
                            <IconButton onClick={() => openDeployDialog(target._id)}>{renderIcon(Rocket)}</IconButton>
                              <IconButton aria-label={`Edit target ${target.name}`} onClick={() => {
                                setTargetForm({
                                id: target._id,
                                name: target.name,
                                slug: target.slug,
                                customer: asNamedEntity(target.customer)?._id || '',
                                server: asNamedEntity(target.server)?._id || '',
                                repository: asNamedEntity(target.repository)?._id || '',
                                environment: target.environment,
                                deploymentStrategy: target.deploymentStrategy,
                                deployJobName: target.deployJobName || '',
                                setupJobName: target.setupJobName || '',
                                setupFamily: target.setupFamily || '',
                                allowedDeploymentModes: target.allowedDeploymentModes?.join(', ') || '',
                                defaultDeploymentMode: target.defaultDeploymentMode || '',
                                modeJobMappingJson: stringifyJson(target.modeJobMapping || []),
                                versionStrategy: target.versionStrategy,
                                defaultConfigTemplate: asNamedEntity(target.defaultConfigTemplate)?._id || '',
                                defaultConfigInstance: asNamedEntity(target.defaultConfigInstance)?._id || '',
                                stackBundleKey: target.stackBundleKey || '',
                                stackPhase: String(target.stackPhase ?? ''),
                                dependencyRepositoryKeys: target.dependencyRepositoryKeys?.join(', ') || '',
                                healthcheckProfile: target.healthcheckProfile || '',
                                healthcheckUrl: target.healthcheckUrl || '',
                                enabled: target.enabled,
                                tags: target.tags.join(', '),
                                deployPath: target.remoteSettings?.deployPath || '',
                                composeFilePath: target.remoteSettings?.composeFilePath || '',
                                composeEnvFile: target.remoteSettings?.composeEnvFile || '',
                                composeNetworkName: target.remoteSettings?.composeNetworkName || '',
                                dockerServiceName: target.remoteSettings?.dockerServiceName || '',
                                runtimeEnvFilePath: target.remoteSettings?.runtimeEnvFilePath || '',
                                remoteServiceRestartCommand: target.remoteSettings?.remoteServiceRestartCommand || '',
                                setupDomain: getMetadataString(target.metadata, 'setupDomain'),
                                setupAppUser: getMetadataString(target.metadata, 'setupAppUser'),
                                setupPublicPort: getMetadataString(target.metadata, 'setupPublicPort'),
                                setupPrivatePort: getMetadataString(target.metadata, 'setupPrivatePort'),
                                setupNginxUpstreamPort: String(target.setupNginxUpstreamPort ?? ''),
                                setupTlsEnabled: getMetadataBooleanString(target.metadata, 'setupTlsEnabled'),
                                setupNginxSiteName: getMetadataString(target.metadata, 'setupNginxSiteName'),
                                acmeEmail: getMetadataString(target.metadata, 'acmeEmail'),
                                gcpProfileScope: target.gcpSetupProfile?.scope || 'shared',
                                gcpAppCode: target.gcpSetupProfile?.appCode || '',
                                gcpCustomerCode: target.gcpSetupProfile?.customerCode || '',
                                gcpRobotCode: target.gcpSetupProfile?.robotCode || '',
                                configParameterRole: target.gcpSetupProfile?.configParameterRole || 'config',
                                credentialsHostPath: target.gcpSetupProfile?.credentialsHostPath || '',
                                catalogPath: target.gcpSetupProfile?.catalogPath || '',
                                publicConfigParameterId: target.gcpSetupProfile?.publicConfigParameterId || '',
                                pipelineVariablesJson: stringifyJson(target.pipelineVariables || {}),
                              });
                                setTargetDialogOpen(true);
                              }}>{renderIcon(Save)}</IconButton>
                              <IconButton aria-label={`Delete target ${target.name}`} color="error" onClick={() => handleDelete(`deployment target ${target.name}`, () => deploymentDashboardApi.deleteDeploymentTarget(target._id).then(() => undefined))}>
                                {renderIcon(Trash2)}
                              </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {activeTab === 'configs' && (
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>Secrets and Runtime Config</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Store secret and parameter references in GCP, then compose env-style runtime bundles with reusable templates and instances.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button variant="outlined" startIcon={renderIcon(ShieldCheck)} onClick={() => { setSecretForm(initialSecretForm); setSecretDialogOpen(true); }}>
                      New Secret Ref
                    </Button>
                    <Button variant="outlined" startIcon={renderIcon(Database)} onClick={() => { setParameterForm(initialParameterForm); setParameterDialogOpen(true); }}>
                      New Parameter Ref
                    </Button>
                    <Button variant="outlined" startIcon={renderIcon(Package)} onClick={() => { setTemplateForm(initialTemplateForm); setTemplateDialogOpen(true); }}>
                      New Template
                    </Button>
                    <Button variant="contained" startIcon={renderIcon(Database)} onClick={() => { setInstanceForm(initialInstanceForm); setInstanceDialogOpen(true); }}>
                      New Instance
                    </Button>
                  </Stack>
                </Stack>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Use flat GCP ids: <strong>`shared-&lt;env&gt;-&lt;appCode&gt;-&lt;role&gt;`</strong>, <strong>`cust-&lt;customerCode&gt;-&lt;env&gt;-&lt;appCode&gt;-&lt;role&gt;`</strong>, and
                  {' '}<strong>`robot-&lt;customerCode&gt;-&lt;env&gt;-&lt;robotCode&gt;-&lt;appCode&gt;-&lt;role&gt;`</strong>. Keep customer, repository, server, and environment as scoped metadata in these records rather than encoding them as slash-style ids.
                </Alert>

                <Typography variant="h6" sx={{ mb: 1.5 }}>Secret References</Typography>
                <TableContainer sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Ref Key</TableCell>
                        <TableCell>Secret</TableCell>
                        <TableCell>Scope</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Version</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {secretReferences.map((secretReference) => (
                        <TableRow key={secretReference._id}>
                          <TableCell>{secretReference.name}</TableCell>
                          <TableCell>{secretReference.referenceKey || '-'}</TableCell>
                          <TableCell>{secretReference.secretId}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatScopedReference(
                                secretReference.customer,
                                secretReference.repository,
                                secretReference.server,
                                secretReference.environment
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5} alignItems="flex-start">
                              <Typography variant="body2">{secretReference.secretType}</Typography>
                              {secretReference.allowHostFileMaterialization ? (
                                <Chip size="small" color="warning" variant="outlined" label="Host file allowed" />
                              ) : null}
                            </Stack>
                          </TableCell>
                          <TableCell>{secretReference.secretVersion}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                              <IconButton
                                disabled={secretReference.allowHostFileMaterialization === true && !isAdmin}
                                onClick={() => {
                                setSecretForm({
                                  id: secretReference._id,
                                  name: secretReference.name,
                                  description: secretReference.description || '',
                                  gcpProjectId: secretReference.gcpProjectId || '',
                                  secretId: secretReference.secretId,
                                  secretVersion: secretReference.secretVersion,
                                  referenceKey: secretReference.referenceKey || '',
                                  secretType: secretReference.secretType,
                                  customer: asNamedEntity(secretReference.customer)?._id || '',
                                  repository: asNamedEntity(secretReference.repository)?._id || '',
                                  server: asNamedEntity(secretReference.server)?._id || '',
                                  environment: secretReference.environment || '',
                                  allowGlobalFallback: secretReference.allowGlobalFallback !== false,
                                  allowHostFileMaterialization: secretReference.allowHostFileMaterialization === true,
                                  labels: secretReference.labels.join(', '),
                                  isActive: secretReference.isActive,
                                });
                                setSecretDialogOpen(true);
                                }}
                              >
                                {renderIcon(Save)}
                              </IconButton>
                              <IconButton
                                color="error"
                                disabled={secretReference.allowHostFileMaterialization === true && !isAdmin}
                                onClick={() => handleDelete(`secret reference ${secretReference.name}`, () => deploymentDashboardApi.deleteSecretReference(secretReference._id).then(() => undefined))}
                              >
                                {renderIcon(Trash2)}
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" sx={{ mb: 1.5 }}>Parameter References</Typography>
                <TableContainer sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Ref Key</TableCell>
                        <TableCell>Parameter</TableCell>
                        <TableCell>Scope</TableCell>
                        <TableCell>Version</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parameterReferences.map((parameterReference) => (
                        <TableRow key={parameterReference._id}>
                          <TableCell>{parameterReference.name}</TableCell>
                          <TableCell>{parameterReference.referenceKey || '-'}</TableCell>
                          <TableCell>{parameterReference.parameterId}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatScopedReference(
                                parameterReference.customer,
                                parameterReference.repository,
                                parameterReference.server,
                                parameterReference.environment
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>{parameterReference.parameterVersion}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                              <IconButton onClick={() => {
                                setParameterForm({
                                  id: parameterReference._id,
                                  name: parameterReference.name,
                                  description: parameterReference.description || '',
                                  gcpProjectId: parameterReference.gcpProjectId || '',
                                  location: parameterReference.location || 'global',
                                  parameterId: parameterReference.parameterId,
                                  parameterVersion: parameterReference.parameterVersion,
                                  referenceKey: parameterReference.referenceKey || '',
                                  parameterFormat: parameterReference.parameterFormat || 'text',
                                  customer: asNamedEntity(parameterReference.customer)?._id || '',
                                  repository: asNamedEntity(parameterReference.repository)?._id || '',
                                  server: asNamedEntity(parameterReference.server)?._id || '',
                                  environment: parameterReference.environment || '',
                                  allowGlobalFallback: parameterReference.allowGlobalFallback !== false,
                                  labels: parameterReference.labels.join(', '),
                                  isActive: parameterReference.isActive,
                                });
                                setParameterDialogOpen(true);
                              }}>
                                {renderIcon(Save)}
                              </IconButton>
                              <IconButton color="error" onClick={() => handleDelete(`parameter reference ${parameterReference.name}`, () => deploymentDashboardApi.deleteParameterReference(parameterReference._id).then(() => undefined))}>
                                {renderIcon(Trash2)}
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" sx={{ mb: 1.5 }}>Config Templates</Typography>
                <TableContainer sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Scope</TableCell>
                        <TableCell>Variables</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {runtimeTemplates.map((template) => (
                        <TableRow key={template._id}>
                          <TableCell>{template.name}</TableCell>
                          <TableCell>{getRepositoryName(template.repository)} / {template.environment || 'all envs'}</TableCell>
                          <TableCell>{template.variables.length}</TableCell>
                          <TableCell><Chip size="small" label={template.isActive ? 'active' : 'inactive'} color={template.isActive ? 'success' : 'default'} /></TableCell>
                          <TableCell align="right">
                            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                              <IconButton onClick={() => {
                                setTemplateForm({
                                  id: template._id,
                                  name: template.name,
                                  slug: template.slug,
                                  description: template.description || '',
                                  customer: asNamedEntity(template.customer)?._id || '',
                                  server: asNamedEntity(template.server)?._id || '',
                                  repository: asNamedEntity(template.repository)?._id || '',
                                  environment: template.environment || '',
                                  tags: template.tags.join(', '),
                                  variablesJson: stringifyJson(template.variables),
                                  isActive: template.isActive,
                                });
                                setTemplateDialogOpen(true);
                              }}>{renderIcon(Save)}</IconButton>
                              <IconButton color="error" onClick={() => handleDelete(`configuration template ${template.name}`, () => deploymentDashboardApi.deleteRuntimeConfigTemplate(template._id).then(() => undefined))}>
                                {renderIcon(Trash2)}
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="h6" sx={{ mb: 1.5 }}>Config Instances</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Template</TableCell>
                        <TableCell>Target</TableCell>
                        <TableCell>Overrides</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {runtimeInstances.map((instance) => (
                        <TableRow key={instance._id}>
                          <TableCell>{instance.name}</TableCell>
                          <TableCell>{asNamedEntity(instance.template)?.name || '-'}</TableCell>
                          <TableCell>{asNamedEntity(instance.deploymentTarget)?.name || getServerName(instance.server)}</TableCell>
                          <TableCell>{instance.overrides.length}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                              <IconButton onClick={() => {
                                setInstanceForm({
                                  id: instance._id,
                                  name: instance.name,
                                  template: asNamedEntity(instance.template)?._id || '',
                                  customer: asNamedEntity(instance.customer)?._id || '',
                                  server: asNamedEntity(instance.server)?._id || '',
                                  repository: asNamedEntity(instance.repository)?._id || '',
                                  deploymentTarget: asNamedEntity(instance.deploymentTarget)?._id || '',
                                  environment: instance.environment || '',
                                  overridesJson: stringifyJson(instance.overrides),
                                  isActive: instance.isActive,
                                });
                                setInstanceDialogOpen(true);
                              }}>{renderIcon(Save)}</IconButton>
                              <IconButton color="error" onClick={() => handleDelete(`configuration instance ${instance.name}`, () => deploymentDashboardApi.deleteRuntimeConfigInstance(instance._id).then(() => undefined))}>
                                {renderIcon(Trash2)}
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Stack>
        )}

        {activeTab === 'deployments' && (
          <DeploymentsExplorer
            customers={customers}
            servers={servers}
            repositories={repositories}
            targets={targets}
            refreshKey={deploymentExplorerRefreshKey}
            saving={saving}
            onOpenDeployDialog={() => openDeployDialog()}
            onCacheDeployments={cacheDeploymentRecords}
            onOpenDeploymentDetails={(deploymentId) => { void handleOpenDeploymentDetails(deploymentId); }}
            onRefreshDeployment={handleRefreshDeploymentRecord}
            onComposeStart={handleComposeStartDeployment}
            onComposeStop={handleComposeStopDeployment}
            onOpenRestart={handleOpenRestartDeployment}
            onOpenRollback={handleOpenRollbackDeployment}
          />
        )}

        {activeTab === 'setup' && (
          <SetupPanel
            targets={targets}
            onRefreshTargets={refreshTargets}
            onNotify={(message, severity) => {
              setSetupNotification({
                message,
                severity: severity || 'info',
              });
            }}
          />
        )}
      </Box>

      <Dialog
        open={Boolean(selectedDeploymentId)}
        onClose={() => setSelectedDeploymentId('')}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" gap={1}>
                {renderIcon(Eye, 18)}
                <Typography variant="h6">Deployment Details</Typography>
              </Stack>
              {selectedDeployment && (
                <Typography variant="body2" color="text.secondary">
                  {getRepositoryName(selectedDeployment.repository)} · {getServerName(selectedDeployment.server)}
                </Typography>
              )}
            </Stack>
            <Button
              onClick={() => { void handleRefreshDeploymentDetails(); }}
              size="small"
              variant="outlined"
              startIcon={deploymentDetailsLoading ? <CircularProgress size={16} /> : renderIcon(RefreshCcw, 14)}
              disabled={deploymentDetailsLoading || !selectedDeploymentId}
            >
              Reload
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {deploymentDetailsLoading && !selectedDeployment ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : !selectedDeployment ? (
            <Alert severity="warning">Deployment details could not be loaded.</Alert>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip
                  size="small"
                  color={statusColor(selectedDeployment.status)}
                  label={`Overall: ${selectedDeployment.status}`}
                />
                <Chip size="small" variant="outlined" label={selectedDeployment.actionType} />
                {selectedDeployment.deploymentMode ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="primary"
                    label={formatDeploymentModeLabel(selectedDeployment.deploymentMode)}
                  />
                ) : null}
                <Chip size="small" variant="outlined" label={selectedDeployment.environment} />
                {selectedDeploymentFailureClass ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="warning"
                    label={`Failure: ${selectedDeploymentFailureClass}`}
                  />
                ) : null}
              </Stack>

              {(selectedDeploymentFailureMessage || selectedDeploymentFailureClass) && (
                <Alert severity={selectedDeployment.status === 'failed' ? 'error' : 'warning'}>
                  {selectedDeploymentFailureMessage || formatFailureClassSummary(selectedDeploymentFailureClass)}
                  {selectedDeploymentFailureMessage && selectedDeploymentFailureClass
                    ? ` (${selectedDeploymentFailureClass})`
                    : ''}
                </Alert>
              )}
              {selectedDeployment.status === 'failed' && selectedDeployment.gitlab?.jobStatus === 'success' && (
                <Alert severity="info">
                  The GitLab job succeeded, but the overall deployment did not complete successfully. See the failure details above.
                </Alert>
              )}

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Summary
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                      gap: 1.5,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">Repository</Typography>
                      <Typography variant="body2">{getRepositoryName(selectedDeployment.repository)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Customer</Typography>
                      <Typography variant="body2">{getCustomerName(selectedDeployment.customer)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Server</Typography>
                      <Typography variant="body2">{getServerName(selectedDeployment.server)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Version</Typography>
                      <Typography variant="body2">{selectedDeployment.requestedRelease?.displayVersion || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Job Name</Typography>
                      <Typography variant="body2">{selectedDeployment.gitlab?.jobName || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">GitLab Job Status</Typography>
                      <Typography variant="body2">{selectedDeployment.gitlab?.jobStatus || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Current Step</Typography>
                      <Typography variant="body2">{selectedDeployment.setupProgress?.currentStep || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Failed Step</Typography>
                      <Typography variant="body2">{selectedDeployment.setupProgress?.failedStep || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Started</Typography>
                      <Typography variant="body2">{formatDate(selectedDeployment.startedAt ?? selectedDeployment.createdAt)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Completed</Typography>
                      <Typography variant="body2">{formatDate(selectedDeployment.completedAt)}</Typography>
                    </Box>
                    <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
                      <Typography variant="caption" color="text.secondary">Correlation ID</Typography>
                      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                        {selectedDeployment.correlationId}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Progress
                  </Typography>
                  <Stack spacing={1}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                        gap: 1.5,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">Step Status</Typography>
                        <Typography variant="body2">
                          {formatProgressStatusLabel(selectedDeployment.setupProgress?.currentStepStatus)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Last Progress Update</Typography>
                        <Typography variant="body2">{formatDate(selectedDeployment.setupProgress?.lastProgressAt)}</Typography>
                      </Box>
                    </Box>
                    {selectedDeploymentProgressSummaryLines.map((line) => (
                      <Typography key={`${selectedDeployment._id}-${line}`} variant="body2" color="text.secondary">
                        {line}
                      </Typography>
                    ))}
                    {(selectedDeployment.setupProgress?.completedSteps ?? []).length > 0 && (
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        {(selectedDeployment.setupProgress?.completedSteps ?? []).map((step) => (
                          <Chip key={`${selectedDeployment._id}-${step}`} size="small" variant="outlined" label={step} />
                        ))}
                      </Stack>
                    )}
                    {selectedDeployment.setupProgress?.rawFallback && (
                      <Alert severity="info">
                        The GitLab job trace did not include structured progress markers. This limits job-step details; it does not determine the overall deployment result.
                      </Alert>
                    )}
                    {!selectedDeployment.setupProgress && !selectedDeploymentFailureMessage && (
                      <Typography variant="body2" color="text.secondary">
                        No deployment progress has been recorded yet.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {selectedDeployment.gitlab && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      GitLab
                    </Typography>
                    <Stack spacing={0.75}>
                      <Typography variant="body2">Project ID: {selectedDeployment.gitlab.projectId ?? '-'}</Typography>
                      <Typography variant="body2">Pipeline ID: {selectedDeployment.gitlab.pipelineId ?? '-'}</Typography>
                      <Typography variant="body2">Job ID: {selectedDeployment.gitlab.jobId ?? '-'}</Typography>
                      <Typography variant="body2">Ref: {selectedDeployment.gitlab.ref || '-'}</Typography>
                      {selectedDeployment.gitlab.jobWebUrl ? (
                        <Typography
                          component="a"
                          href={selectedDeployment.gitlab.jobWebUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          color="primary"
                          sx={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                          Open job log
                          {renderIcon(ExternalLink, 12)}
                        </Typography>
                      ) : null}
                      {selectedDeployment.gitlab.pipelineWebUrl ? (
                        <Typography
                          component="a"
                          href={selectedDeployment.gitlab.pipelineWebUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          color="primary"
                          sx={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                          Open pipeline
                          {renderIcon(ExternalLink, 12)}
                        </Typography>
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedDeploymentId('')}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(setupNotification)}
        autoHideDuration={5000}
        onClose={() => setSetupNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSetupNotification(null)}
          severity={setupNotification?.severity || 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {setupNotification?.message || ''}
        </Alert>
      </Snackbar>

      <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{customerForm.id ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={customerForm.name} onChange={(event) => setCustomerForm((current) => ({ ...current, name: event.target.value }))} fullWidth />
            <TextField label="Slug" value={customerForm.slug} onChange={(event) => setCustomerForm((current) => ({ ...current, slug: event.target.value }))} fullWidth />
            <TextField label="Description" value={customerForm.description} onChange={(event) => setCustomerForm((current) => ({ ...current, description: event.target.value }))} fullWidth multiline minRows={3} />
            <TextField select label="Status" value={customerForm.status} onChange={(event) => setCustomerForm((current) => ({ ...current, status: event.target.value }))} fullWidth>
              {['active', 'inactive', 'prospect'].map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
            </TextField>
            <TextField label="Tags" value={customerForm.tags} onChange={(event) => setCustomerForm((current) => ({ ...current, tags: event.target.value }))} helperText="Comma-separated tags" fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCustomerSubmit} disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={secretDialogOpen} onClose={() => setSecretDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{secretForm.id ? 'Edit Secret Reference' : 'Add Secret Reference'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Display Name" value={secretForm.name} onChange={(event) => setSecretForm((current) => ({ ...current, name: event.target.value }))} fullWidth />
            <TextField label="GCP Project ID" value={secretForm.gcpProjectId} onChange={(event) => setSecretForm((current) => ({ ...current, gcpProjectId: event.target.value }))} fullWidth />
            <TextField
              label="Secret ID"
              value={secretForm.secretId}
              onChange={(event) => setSecretForm((current) => ({ ...current, secretId: event.target.value }))}
              helperText="Use flat ids such as shared-prod-dtb-gitlab-access-token or cust-acme-prod-cb-jwt-secret. Avoid slash-style paths."
              fullWidth
            />
            <TextField label="Secret Version" value={secretForm.secretVersion} onChange={(event) => setSecretForm((current) => ({ ...current, secretVersion: event.target.value }))} fullWidth />
            <TextField label="Reference Key" value={secretForm.referenceKey} onChange={(event) => setSecretForm((current) => ({ ...current, referenceKey: event.target.value }))} helperText="Optional logical key used for scoped fallback resolution" fullWidth />
            <TextField select label="Secret Type" value={secretForm.secretType} onChange={(event) => setSecretForm((current) => ({ ...current, secretType: event.target.value }))} fullWidth>
              {['ssh-user', 'ssh-private-key', 'ssh-known-hosts', 'ssh-password', 'env', 'api-token', 'custom'].map((secretType) => <MenuItem key={secretType} value={secretType}>{secretType}</MenuItem>)}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField select label="Customer Scope" value={secretForm.customer} onChange={(event) => setSecretForm((current) => ({ ...current, customer: event.target.value }))} fullWidth>
                  <MenuItem value="">Global</MenuItem>
                  {customers.map((customer) => <MenuItem key={customer._id} value={customer._id}>{customer.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select label="Repository Scope" value={secretForm.repository} onChange={(event) => setSecretForm((current) => ({ ...current, repository: event.target.value }))} fullWidth>
                  <MenuItem value="">All repositories</MenuItem>
                  {repositories.map((repository) => <MenuItem key={repository._id} value={repository._id}>{repository.pathWithNamespace}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select label="Server Scope" value={secretForm.server} onChange={(event) => setSecretForm((current) => ({ ...current, server: event.target.value }))} fullWidth>
                  <MenuItem value="">All servers</MenuItem>
                  {servers.map((server) => <MenuItem key={server._id} value={server._id}>{server.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Environment Scope" value={secretForm.environment} onChange={(event) => setSecretForm((current) => ({ ...current, environment: event.target.value }))} helperText="Optional dev / qa / staging / prod" fullWidth />
              </Grid>
            </Grid>
            <TextField label="Labels" value={secretForm.labels} onChange={(event) => setSecretForm((current) => ({ ...current, labels: event.target.value }))} helperText="Comma-separated labels" fullWidth />
            <TextField label="Description" value={secretForm.description} onChange={(event) => setSecretForm((current) => ({ ...current, description: event.target.value }))} fullWidth multiline minRows={3} />
            <FormControlLabel
              control={(
                <Switch
                  checked={secretForm.allowGlobalFallback}
                  disabled={secretForm.allowHostFileMaterialization}
                  onChange={(_, checked) => setSecretForm((current) => ({ ...current, allowGlobalFallback: checked }))}
                />
              )}
              label="Allow fallback to less-specific or global references"
            />
            {isAdmin ? (
              <FormControlLabel
                control={(
                  <Switch
                    checked={secretForm.allowHostFileMaterialization}
                    onChange={(_, checked) => setSecretForm((current) => ({
                      ...current,
                      allowHostFileMaterialization: checked,
                      ...(checked ? { allowGlobalFallback: false } : {}),
                    }))}
                  />
                )}
                label="Allow this reference to be materialized as a managed host file"
              />
            ) : null}
            <FormControlLabel control={<Switch checked={secretForm.isActive} onChange={(_, checked) => setSecretForm((current) => ({ ...current, isActive: checked }))} />} label="Active" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSecretDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSecretSubmit} disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={parameterDialogOpen} onClose={() => setParameterDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{parameterForm.id ? 'Edit Parameter Reference' : 'Add Parameter Reference'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Display Name" value={parameterForm.name} onChange={(event) => setParameterForm((current) => ({ ...current, name: event.target.value }))} fullWidth />
            <TextField label="GCP Project ID" value={parameterForm.gcpProjectId} onChange={(event) => setParameterForm((current) => ({ ...current, gcpProjectId: event.target.value }))} fullWidth />
            <TextField label="Location" value={parameterForm.location} onChange={(event) => setParameterForm((current) => ({ ...current, location: event.target.value }))} fullWidth />
            <TextField
              label="Parameter ID"
              value={parameterForm.parameterId}
              onChange={(event) => setParameterForm((current) => ({ ...current, parameterId: event.target.value }))}
              helperText="Use flat ids such as shared-prod-dab-config, cust-acme-prod-noc-public-config, or robot-acme-prod-r07-rts-public-config."
              fullWidth
            />
            <TextField label="Parameter Version" value={parameterForm.parameterVersion} onChange={(event) => setParameterForm((current) => ({ ...current, parameterVersion: event.target.value }))} fullWidth />
            <TextField label="Reference Key" value={parameterForm.referenceKey} onChange={(event) => setParameterForm((current) => ({ ...current, referenceKey: event.target.value }))} helperText="Optional logical key used for scoped fallback resolution" fullWidth />
            <TextField select label="Format" value={parameterForm.parameterFormat} onChange={(event) => setParameterForm((current) => ({ ...current, parameterFormat: event.target.value }))} fullWidth>
              {['text', 'json', 'yaml'].map((parameterFormat) => <MenuItem key={parameterFormat} value={parameterFormat}>{parameterFormat}</MenuItem>)}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField select label="Customer Scope" value={parameterForm.customer} onChange={(event) => setParameterForm((current) => ({ ...current, customer: event.target.value }))} fullWidth>
                  <MenuItem value="">Global</MenuItem>
                  {customers.map((customer) => <MenuItem key={customer._id} value={customer._id}>{customer.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select label="Repository Scope" value={parameterForm.repository} onChange={(event) => setParameterForm((current) => ({ ...current, repository: event.target.value }))} fullWidth>
                  <MenuItem value="">All repositories</MenuItem>
                  {repositories.map((repository) => <MenuItem key={repository._id} value={repository._id}>{repository.pathWithNamespace}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select label="Server Scope" value={parameterForm.server} onChange={(event) => setParameterForm((current) => ({ ...current, server: event.target.value }))} fullWidth>
                  <MenuItem value="">All servers</MenuItem>
                  {servers.map((server) => <MenuItem key={server._id} value={server._id}>{server.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Environment Scope" value={parameterForm.environment} onChange={(event) => setParameterForm((current) => ({ ...current, environment: event.target.value }))} helperText="Optional dev / qa / staging / prod" fullWidth />
              </Grid>
            </Grid>
            <TextField label="Labels" value={parameterForm.labels} onChange={(event) => setParameterForm((current) => ({ ...current, labels: event.target.value }))} helperText="Comma-separated labels" fullWidth />
            <TextField label="Description" value={parameterForm.description} onChange={(event) => setParameterForm((current) => ({ ...current, description: event.target.value }))} fullWidth multiline minRows={3} />
            <FormControlLabel control={<Switch checked={parameterForm.allowGlobalFallback} onChange={(_, checked) => setParameterForm((current) => ({ ...current, allowGlobalFallback: checked }))} />} label="Allow fallback to less-specific or global references" />
            <FormControlLabel control={<Switch checked={parameterForm.isActive} onChange={(_, checked) => setParameterForm((current) => ({ ...current, isActive: checked }))} />} label="Active" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParameterDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleParameterSubmit} disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={serverDialogOpen} onClose={() => setServerDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{serverForm.id ? 'Edit Server' : 'Add Server'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField select label="Customer" value={serverForm.customer} onChange={(event) => setServerForm((current) => ({ ...current, customer: event.target.value }))} fullWidth>
                {customers.map((customer) => <MenuItem key={customer._id} value={customer._id}>{customer.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Name" value={serverForm.name} onChange={(event) => setServerForm((current) => ({ ...current, name: event.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Slug" value={serverForm.slug} onChange={(event) => setServerForm((current) => ({ ...current, slug: event.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Hostname" value={serverForm.hostname} onChange={(event) => setServerForm((current) => ({ ...current, hostname: event.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="IP Address" value={serverForm.ipAddress} onChange={(event) => setServerForm((current) => ({ ...current, ipAddress: event.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="SSH Port" value={serverForm.sshPort} onChange={(event) => setServerForm((current) => ({ ...current, sshPort: event.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" value={serverForm.description} onChange={(event) => setServerForm((current) => ({ ...current, description: event.target.value }))} fullWidth multiline minRows={3} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Environments" value={serverForm.environments} onChange={(event) => setServerForm((current) => ({ ...current, environments: event.target.value }))} helperText="Comma-separated" fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Tags" value={serverForm.tags} onChange={(event) => setServerForm((current) => ({ ...current, tags: event.target.value }))} helperText="Comma-separated" fullWidth />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select label="SSH User Ref" value={serverForm.sshUser} onChange={(event) => setServerForm((current) => ({ ...current, sshUser: event.target.value }))} fullWidth>
                <MenuItem value="">None</MenuItem>
                {secretReferences.map((secretReference) => <MenuItem key={secretReference._id} value={secretReference._id}>{secretReference.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select label="SSH Key Ref" value={serverForm.sshPrivateKey} onChange={(event) => setServerForm((current) => ({ ...current, sshPrivateKey: event.target.value }))} fullWidth>
                <MenuItem value="">None</MenuItem>
                {secretReferences.map((secretReference) => <MenuItem key={secretReference._id} value={secretReference._id}>{secretReference.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select label="Known Hosts Ref" value={serverForm.sshKnownHosts} onChange={(event) => setServerForm((current) => ({ ...current, sshKnownHosts: event.target.value }))} fullWidth>
                <MenuItem value="">None</MenuItem>
                {secretReferences.map((secretReference) => <MenuItem key={secretReference._id} value={secretReference._id}>{secretReference.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select label="Password Ref" value={serverForm.sshPassword} onChange={(event) => setServerForm((current) => ({ ...current, sshPassword: event.target.value }))} fullWidth>
                <MenuItem value="">None</MenuItem>
                {secretReferences.map((secretReference) => <MenuItem key={secretReference._id} value={secretReference._id}>{secretReference.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}><TextField label="Deploy Path" value={serverForm.deployPath} onChange={(event) => setServerForm((current) => ({ ...current, deployPath: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Compose File Path" value={serverForm.composeFilePath} onChange={(event) => setServerForm((current) => ({ ...current, composeFilePath: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Compose Env File" value={serverForm.composeEnvFile} onChange={(event) => setServerForm((current) => ({ ...current, composeEnvFile: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Compose Network Name" value={serverForm.composeNetworkName} onChange={(event) => setServerForm((current) => ({ ...current, composeNetworkName: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Docker Service Name" value={serverForm.dockerServiceName} onChange={(event) => setServerForm((current) => ({ ...current, dockerServiceName: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Runtime Env File Path" value={serverForm.runtimeEnvFilePath} onChange={(event) => setServerForm((current) => ({ ...current, runtimeEnvFilePath: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Healthcheck URL" value={serverForm.healthcheckUrl} onChange={(event) => setServerForm((current) => ({ ...current, healthcheckUrl: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={4}>
              <TextField select label="Privilege Model" value={serverForm.privilegeModel} onChange={(event) => setServerForm((current) => ({ ...current, privilegeModel: event.target.value }))} fullWidth>
                {['unknown', 'nopasswd-sudo', 'password-sudo'].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select label="OS Family" value={serverForm.osFamily} onChange={(event) => setServerForm((current) => ({ ...current, osFamily: event.target.value }))} fullWidth>
                {['unknown', 'debian', 'rhel', 'alpine'].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select label="Package Manager" value={serverForm.packageManager} onChange={(event) => setServerForm((current) => ({ ...current, packageManager: event.target.value }))} fullWidth>
                {['unknown', 'apt', 'yum', 'dnf', 'apk'].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Supported Modes" value={serverForm.supportedModes} onChange={(event) => setServerForm((current) => ({ ...current, supportedModes: event.target.value }))} helperText="Comma-separated: docker, static, package, appimage" fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Credential Source Path" value={serverForm.gcpCredentialsBasePath} onChange={(event) => setServerForm((current) => ({ ...current, gcpCredentialsBasePath: event.target.value }))} helperText="Optional control-plane service-account JSON path. Used only when a target opts into credential staging." fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Allowed Credential Copy Target IDs" value={serverForm.allowedCredentialCopyTargetIds} onChange={(event) => setServerForm((current) => ({ ...current, allowedCredentialCopyTargetIds: event.target.value }))} helperText="Comma-separated deployment target ids allowed to receive credentials from this server bootstrap source" fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel control={<Switch checked={serverForm.nginxManaged} onChange={(_, checked) => setServerForm((current) => ({ ...current, nginxManaged: checked }))} />} label="Host Nginx is managed by setup" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel control={<Switch checked={serverForm.tlsEnabled} onChange={(_, checked) => setServerForm((current) => ({ ...current, tlsEnabled: checked }))} />} label="TLS is expected on this host" />
            </Grid>
            <Grid item xs={12}><TextField label="Restart Command" value={serverForm.remoteServiceRestartCommand} onChange={(event) => setServerForm((current) => ({ ...current, remoteServiceRestartCommand: event.target.value }))} fullWidth multiline minRows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServerDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleServerSubmit} disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{templateForm.id ? 'Edit Runtime Config Template' : 'Add Runtime Config Template'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={templateForm.name} onChange={(event) => setTemplateForm((current) => ({ ...current, name: event.target.value }))} fullWidth />
            <TextField label="Slug" value={templateForm.slug} onChange={(event) => setTemplateForm((current) => ({ ...current, slug: event.target.value }))} fullWidth />
            <TextField label="Description" value={templateForm.description} onChange={(event) => setTemplateForm((current) => ({ ...current, description: event.target.value }))} fullWidth multiline minRows={2} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}><TextField select label="Customer Scope" value={templateForm.customer} onChange={(event) => setTemplateForm((current) => ({ ...current, customer: event.target.value }))} fullWidth><MenuItem value="">All customers</MenuItem>{customers.map((customer) => <MenuItem key={customer._id} value={customer._id}>{customer.name}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={4}><TextField select label="Server Scope" value={templateForm.server} onChange={(event) => setTemplateForm((current) => ({ ...current, server: event.target.value }))} fullWidth><MenuItem value="">All servers</MenuItem>{servers.map((server) => <MenuItem key={server._id} value={server._id}>{server.name}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={4}><TextField select label="Repository Scope" value={templateForm.repository} onChange={(event) => setTemplateForm((current) => ({ ...current, repository: event.target.value }))} fullWidth><MenuItem value="">All repositories</MenuItem>{repositories.map((repository) => <MenuItem key={repository._id} value={repository._id}>{repository.pathWithNamespace}</MenuItem>)}</TextField></Grid>
            </Grid>
            <TextField label="Environment Scope" value={templateForm.environment} onChange={(event) => setTemplateForm((current) => ({ ...current, environment: event.target.value }))} fullWidth />
            <TextField label="Tags" value={templateForm.tags} onChange={(event) => setTemplateForm((current) => ({ ...current, tags: event.target.value }))} helperText="Comma-separated" fullWidth />
            <TextField label="Variables JSON" value={templateForm.variablesJson} onChange={(event) => setTemplateForm((current) => ({ ...current, variablesJson: event.target.value }))} error={Boolean(templateVariablesValidation.error)} fullWidth multiline minRows={8} helperText={templateVariablesValidation.error || 'Example: [{"key":"NODE_ENV","valueSource":"plain","plainValue":"production"},{"key":"DATABASE_URL","valueSource":"secretReference","secretReference":"<secretRefId>"},{"key":"API_BASE_URL","valueSource":"parameterReference","parameterReference":"<parameterRefId>","referenceKey":"api-base-url"}]'} />
            <FormControlLabel control={<Switch checked={templateForm.isActive} onChange={(_, checked) => setTemplateForm((current) => ({ ...current, isActive: checked }))} />} label="Active template" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleTemplateSubmit} disabled={saving || Boolean(templateVariablesValidation.error)}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={instanceDialogOpen} onClose={() => setInstanceDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{instanceForm.id ? 'Edit Runtime Config Instance' : 'Add Runtime Config Instance'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={instanceForm.name} onChange={(event) => setInstanceForm((current) => ({ ...current, name: event.target.value }))} fullWidth />
            <TextField select label="Template" value={instanceForm.template} onChange={(event) => setInstanceForm((current) => ({ ...current, template: event.target.value }))} fullWidth>
              {runtimeTemplates.map((template) => <MenuItem key={template._id} value={template._id}>{template.name}</MenuItem>)}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}><TextField select label="Customer" value={instanceForm.customer} onChange={(event) => setInstanceForm((current) => ({ ...current, customer: event.target.value }))} fullWidth><MenuItem value="">Optional</MenuItem>{customers.map((customer) => <MenuItem key={customer._id} value={customer._id}>{customer.name}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={3}><TextField select label="Server" value={instanceForm.server} onChange={(event) => setInstanceForm((current) => ({ ...current, server: event.target.value }))} fullWidth><MenuItem value="">Optional</MenuItem>{servers.map((server) => <MenuItem key={server._id} value={server._id}>{server.name}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={3}><TextField select label="Repository" value={instanceForm.repository} onChange={(event) => setInstanceForm((current) => ({ ...current, repository: event.target.value }))} fullWidth><MenuItem value="">Optional</MenuItem>{repositories.map((repository) => <MenuItem key={repository._id} value={repository._id}>{repository.pathWithNamespace}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={3}><TextField select label="Target" value={instanceForm.deploymentTarget} onChange={(event) => setInstanceForm((current) => ({ ...current, deploymentTarget: event.target.value }))} fullWidth><MenuItem value="">Optional</MenuItem>{targets.map((target) => <MenuItem key={target._id} value={target._id}>{target.name}</MenuItem>)}</TextField></Grid>
            </Grid>
            <TextField label="Environment Scope" value={instanceForm.environment} onChange={(event) => setInstanceForm((current) => ({ ...current, environment: event.target.value }))} fullWidth />
            <TextField label="Overrides JSON" value={instanceForm.overridesJson} onChange={(event) => setInstanceForm((current) => ({ ...current, overridesJson: event.target.value }))} error={Boolean(instanceOverridesValidation.error)} fullWidth multiline minRows={8} helperText={instanceOverridesValidation.error || 'Example: [{"key":"CUSTOMER_NAME","valueSource":"plain","plainValue":"Acme"},{"key":"PAYMENT_PROVIDER_KEY","valueSource":"parameterReference","referenceKey":"payment-provider-key"}]'} />
            <FormControlLabel control={<Switch checked={instanceForm.isActive} onChange={(_, checked) => setInstanceForm((current) => ({ ...current, isActive: checked }))} />} label="Active instance" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstanceDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleInstanceSubmit} disabled={saving || Boolean(instanceOverridesValidation.error)}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={targetDialogOpen} onClose={() => setTargetDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{targetForm.id ? 'Edit Deployment Target' : 'Add Deployment Target'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}><TextField label="Name" value={targetForm.name} onChange={(event) => setTargetForm((current) => ({ ...current, name: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Slug" value={targetForm.slug} onChange={(event) => setTargetForm((current) => ({ ...current, slug: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={4}><TextField select label="Customer" value={targetForm.customer} onChange={(event) => setTargetForm((current) => ({ ...current, customer: event.target.value }))} fullWidth>{customers.map((customer) => <MenuItem key={customer._id} value={customer._id}>{customer.name}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={4}><TextField select label="Server" value={targetForm.server} onChange={(event) => setTargetForm((current) => ({ ...current, server: event.target.value }))} fullWidth>{servers.map((server) => <MenuItem key={server._id} value={server._id}>{server.name}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={4}><TextField select label="Repository" value={targetForm.repository} onChange={(event) => setTargetForm((current) => ({ ...current, repository: event.target.value }))} fullWidth>{repositories.map((repository) => <MenuItem key={repository._id} value={repository._id}>{repository.pathWithNamespace}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={3}><TextField select label="Environment" value={targetForm.environment} onChange={(event) => setTargetForm((current) => ({ ...current, environment: event.target.value }))} fullWidth>{['dev', 'qa', 'staging', 'prod'].map((environment) => <MenuItem key={environment} value={environment}>{environment}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={3}><TextField select label="Strategy" value={targetForm.deploymentStrategy} onChange={(event) => setTargetForm((current) => ({ ...current, deploymentStrategy: event.target.value }))} fullWidth>{['template-manual', 'legacy-adapter'].map((strategy) => <MenuItem key={strategy} value={strategy}>{strategy}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={3}><TextField select label="Version Strategy" value={targetForm.versionStrategy} onChange={(event) => setTargetForm((current) => ({ ...current, versionStrategy: event.target.value }))} fullWidth>{['auto', 'tag', 'image', 'package', 'branch', 'sha'].map((strategy) => <MenuItem key={strategy} value={strategy}>{strategy}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={3}><TextField label="Deploy Job Name" value={targetForm.deployJobName} onChange={(event) => setTargetForm((current) => ({ ...current, deployJobName: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="Setup Job Name" value={targetForm.setupJobName} onChange={(event) => setTargetForm((current) => ({ ...current, setupJobName: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={3}>
              <TextField select label="Setup Family" value={targetForm.setupFamily} onChange={(event) => setTargetForm((current) => ({ ...current, setupFamily: event.target.value }))} fullWidth>
                {['', 'docker', 'static', 'package', 'appimage'].map((value) => (
                  <MenuItem key={value || 'blank'} value={value}>{value || 'None'}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}><TextField label="Allowed Deployment Modes" value={targetForm.allowedDeploymentModes} onChange={(event) => setTargetForm((current) => ({ ...current, allowedDeploymentModes: event.target.value }))} helperText="Comma-separated: docker, static, package, appimage" fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="Default Deployment Mode" value={targetForm.defaultDeploymentMode} onChange={(event) => setTargetForm((current) => ({ ...current, defaultDeploymentMode: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField select label="Default Template" value={targetForm.defaultConfigTemplate} onChange={(event) => setTargetForm((current) => ({ ...current, defaultConfigTemplate: event.target.value }))} fullWidth><MenuItem value="">None</MenuItem>{runtimeTemplates.map((template) => <MenuItem key={template._id} value={template._id}>{template.name}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={6}><TextField select label="Default Instance" value={targetForm.defaultConfigInstance} onChange={(event) => setTargetForm((current) => ({ ...current, defaultConfigInstance: event.target.value }))} fullWidth><MenuItem value="">None</MenuItem>{runtimeInstances.map((instance) => <MenuItem key={instance._id} value={instance._id}>{instance.name}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={6}><TextField label="Stack Bundle Key" value={targetForm.stackBundleKey} onChange={(event) => setTargetForm((current) => ({ ...current, stackBundleKey: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="Stack Phase" value={targetForm.stackPhase} onChange={(event) => setTargetForm((current) => ({ ...current, stackPhase: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="Healthcheck Profile" value={targetForm.healthcheckProfile} onChange={(event) => setTargetForm((current) => ({ ...current, healthcheckProfile: event.target.value }))} helperText='Examples: default, docker-default, optional, appimage' fullWidth /></Grid>
            <Grid item xs={12}><TextField label="Dependency Repository Keys" value={targetForm.dependencyRepositoryKeys} onChange={(event) => setTargetForm((current) => ({ ...current, dependencyRepositoryKeys: event.target.value }))} helperText="Comma-separated repository keys" fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Healthcheck URL" value={targetForm.healthcheckUrl} onChange={(event) => setTargetForm((current) => ({ ...current, healthcheckUrl: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Tags" value={targetForm.tags} onChange={(event) => setTargetForm((current) => ({ ...current, tags: event.target.value }))} helperText="Comma-separated" fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Deploy Path" value={targetForm.deployPath} onChange={(event) => setTargetForm((current) => ({ ...current, deployPath: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Compose File Path" value={targetForm.composeFilePath} onChange={(event) => setTargetForm((current) => ({ ...current, composeFilePath: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Compose Env File" value={targetForm.composeEnvFile} onChange={(event) => setTargetForm((current) => ({ ...current, composeEnvFile: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Compose Network Name" value={targetForm.composeNetworkName} onChange={(event) => setTargetForm((current) => ({ ...current, composeNetworkName: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Docker Service Name" value={targetForm.dockerServiceName} onChange={(event) => setTargetForm((current) => ({ ...current, dockerServiceName: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Runtime Env File Path" value={targetForm.runtimeEnvFilePath} onChange={(event) => setTargetForm((current) => ({ ...current, runtimeEnvFilePath: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Restart Command" value={targetForm.remoteServiceRestartCommand} onChange={(event) => setTargetForm((current) => ({ ...current, remoteServiceRestartCommand: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="Mode Job Mapping JSON" value={targetForm.modeJobMappingJson} onChange={(event) => setTargetForm((current) => ({ ...current, modeJobMappingJson: event.target.value }))} error={Boolean(targetModeJobMappingValidation.error)} helperText={targetModeJobMappingValidation.error || 'Example: [{"mode":"docker","setupJobName":"setup_dev_docker","deployJobName":"deploy_dev_docker"}]'} fullWidth multiline minRows={4} /></Grid>
            <Grid item xs={12} md={4}><TextField label="Setup Domain" value={targetForm.setupDomain} onChange={(event) => setTargetForm((current) => ({ ...current, setupDomain: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={4}><TextField label="Setup App User" value={targetForm.setupAppUser} onChange={(event) => setTargetForm((current) => ({ ...current, setupAppUser: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={4}><TextField label="ACME Email" value={targetForm.acmeEmail} onChange={(event) => setTargetForm((current) => ({ ...current, acmeEmail: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="Public Port" value={targetForm.setupPublicPort} onChange={(event) => setTargetForm((current) => ({ ...current, setupPublicPort: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="Private Port" value={targetForm.setupPrivatePort} onChange={(event) => setTargetForm((current) => ({ ...current, setupPrivatePort: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="Nginx Upstream Port" value={targetForm.setupNginxUpstreamPort} onChange={(event) => setTargetForm((current) => ({ ...current, setupNginxUpstreamPort: event.target.value }))} helperText={isAdmin ? 'Leave blank for ordinary applications. An explicit port enables the managed MQTT transport.' : 'Only administrators can change the managed MQTT upstream.'} InputProps={{ readOnly: !isAdmin }} inputProps={{ inputMode: 'numeric' }} fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="TLS Enabled" value={targetForm.setupTlsEnabled} onChange={(event) => setTargetForm((current) => ({ ...current, setupTlsEnabled: event.target.value }))} helperText='Use "true" or "false" to override auto-detection' fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="Nginx Site Name" value={targetForm.setupNginxSiteName} onChange={(event) => setTargetForm((current) => ({ ...current, setupNginxSiteName: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={3}>
              <TextField select label="GCP Profile Scope" value={targetForm.gcpProfileScope} onChange={(event) => setTargetForm((current) => ({ ...current, gcpProfileScope: event.target.value }))} fullWidth>
                {['shared', 'cust', 'robot'].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}><TextField label="App Code" value={targetForm.gcpAppCode} onChange={(event) => setTargetForm((current) => ({ ...current, gcpAppCode: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="Customer Code" value={targetForm.gcpCustomerCode} onChange={(event) => setTargetForm((current) => ({ ...current, gcpCustomerCode: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="Robot Code" value={targetForm.gcpRobotCode} onChange={(event) => setTargetForm((current) => ({ ...current, gcpRobotCode: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={4}><TextField label="Config Parameter Role" value={targetForm.configParameterRole} onChange={(event) => setTargetForm((current) => ({ ...current, configParameterRole: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12} md={4}><TextField label="Credentials Host Path" value={targetForm.credentialsHostPath} onChange={(event) => setTargetForm((current) => ({ ...current, credentialsHostPath: event.target.value }))} helperText="Leave blank unless this target needs dashboard-managed credential staging." fullWidth /></Grid>
            <Grid item xs={12} md={4}><TextField label="Catalog Path" value={targetForm.catalogPath} onChange={(event) => setTargetForm((current) => ({ ...current, catalogPath: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="Public Config Parameter ID" value={targetForm.publicConfigParameterId} onChange={(event) => setTargetForm((current) => ({ ...current, publicConfigParameterId: event.target.value }))} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="Extra Pipeline Variables JSON" value={targetForm.pipelineVariablesJson} onChange={(event) => setTargetForm((current) => ({ ...current, pipelineVariablesJson: event.target.value }))} error={Boolean(targetPipelineVariablesValidation.error)} helperText={targetPipelineVariablesValidation.error || 'Example: {"DASHBOARD_TRIGGER":"true","DEPLOY_REGION":"us-central1"}'} fullWidth multiline minRows={5} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={targetForm.enabled} onChange={(_, checked) => setTargetForm((current) => ({ ...current, enabled: checked }))} />} label="Enabled target" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTargetDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleTargetSubmit} disabled={saving || Boolean(targetPipelineVariablesValidation.error) || Boolean(targetModeJobMappingValidation.error)}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={repositoryDialogOpen} onClose={() => setRepositoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Register GitLab Repository</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="GitLab Project ID" value={repositoryForm.gitlabProjectId} onChange={(event) => setRepositoryForm((current) => ({ ...current, gitlabProjectId: event.target.value }))} helperText="Use either Project ID or namespace path" fullWidth />
            <TextField label="Path With Namespace" value={repositoryForm.pathWithNamespace} onChange={(event) => setRepositoryForm((current) => ({ ...current, pathWithNamespace: event.target.value }))} helperText="Example: group/subgroup/project" fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRepositoryDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRepositorySubmit} disabled={saving}>Register</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(repositoryOverrideDialogRepositoryId)} onClose={closeRepositoryOverrideDialog} maxWidth="md" fullWidth>
        <DialogTitle>Inspect Repo Overrides</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack spacing={0.5}>
              <Typography variant="body1" fontWeight={600}>
                {selectedRepositoryOverrideRecord?.pathWithNamespace || 'Repository not found'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manifest status: {selectedRepositoryOverrideRecord?.deploymentManifest?.status || 'unknown'}
              </Typography>
            </Stack>
            <Alert severity={selectedRepositoryOverrideEntries.length > 0 ? 'warning' : 'success'}>
              {selectedRepositoryOverrideEntries.length > 0
                ? 'Repo-level break-glass overrides are taking precedence over manifest values for the fields below.'
                : 'This repository is currently using manifest values only. No active break-glass overrides remain.'}
            </Alert>
            <TextField
              label="Reason for clear action"
              value={repositoryOverrideReason}
              onChange={(event) => setRepositoryOverrideReason(event.target.value)}
              helperText="Saved in the override audit trail. Keep a short note even when clearing stale overrides."
              fullWidth
            />
            {selectedRepositoryOverrideEntries.map((entry) => {
              const clearable = isRepositoryOverrideField(entry.field);
              const actorLabel = entry.metadata?.actor?.name || entry.metadata?.actor?.email || '';

              return (
                <Card key={entry.field} variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          <Chip size="small" label={entry.field} />
                          <Chip size="small" color="warning" label="Override active" />
                        </Stack>
                        {clearable && (
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            startIcon={renderIcon(Undo2, 16)}
                            onClick={() => handleClearRepositoryOverride(entry.field)}
                            disabled={saving || !repositoryOverrideReason.trim()}
                          >
                            Clear Field
                          </Button>
                        )}
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">Manifest value</Typography>
                          <Box
                            component="pre"
                            sx={{
                              mt: 0.75,
                              mb: 0,
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: 'action.hover',
                              fontFamily: 'monospace',
                              fontSize: 12,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {formatOverrideValue(entry.baseValue)}
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">Current override</Typography>
                          <Box
                            component="pre"
                            sx={{
                              mt: 0.75,
                              mb: 0,
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: 'warning.50',
                              fontFamily: 'monospace',
                              fontSize: 12,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {formatOverrideValue(entry.value)}
                          </Box>
                        </Grid>
                      </Grid>
                      <Stack spacing={0.35}>
                        <Typography variant="caption" color="text.secondary">
                          Reason: {entry.metadata?.reason || 'No reason recorded'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Updated: {formatDate(entry.metadata?.updatedAt)}
                          {entry.metadata?.reviewAt ? ` • Review ${formatDate(entry.metadata.reviewAt)}` : ''}
                          {entry.metadata?.expiresAt ? ` • Expires ${formatDate(entry.metadata.expiresAt)}` : ''}
                        </Typography>
                        {actorLabel && (
                          <Typography variant="caption" color="text.secondary">
                            Actor: {actorLabel}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRepositoryOverrideDialog}>Close</Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={renderIcon(Undo2, 16)}
            onClick={handleClearAllRepositoryOverrides}
            disabled={saving || selectedRepositoryOverrideEntries.length === 0 || !repositoryOverrideReason.trim()}
          >
            Clear All Overrides
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deployDialogOpen} onClose={() => setDeployDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Launch Deployment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              select
              label="Deployment Target"
              value={deployForm.deploymentTargetId}
              onChange={(event) => {
                const nextTarget = targets.find((target) => target._id === event.target.value) || null;
                setDeployForm((current) => ({
                  ...current,
                  deploymentTargetId: event.target.value,
                  deploymentMode: resolveDefaultDeploymentMode(nextTarget),
                  sourceKey: '',
                }));
              }}
              fullWidth
            >
              <MenuItem value="">Select a target</MenuItem>
              {targets.filter((target) => target.enabled).map((target) => (
                <MenuItem key={target._id} value={target._id}>
                  {target.name} • {target.environment}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Version / Tag / Package / Image"
              value={deployForm.sourceKey}
              onChange={(event) => setDeployForm((current) => ({ ...current, sourceKey: event.target.value }))}
              fullWidth
              disabled={!deployForm.deploymentTargetId}
            >
              <MenuItem value="">Select a release option</MenuItem>
              {releaseChoiceOptions.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {(selectedDeploymentTarget?.allowedDeploymentModes?.length ?? 0) > 0 && (
              <TextField
                select
                label="Deployment Mode"
                value={deployForm.deploymentMode}
                onChange={(event) => setDeployForm((current) => ({ ...current, deploymentMode: event.target.value }))}
                fullWidth
                disabled={!deployForm.deploymentTargetId}
              >
                {(selectedDeploymentTarget?.allowedDeploymentModes ?? []).map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {formatDeploymentModeLabel(mode)}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField select label="Override Config Template" value={deployForm.runtimeConfigTemplateId} onChange={(event) => setDeployForm((current) => ({ ...current, runtimeConfigTemplateId: event.target.value }))} fullWidth>
                  <MenuItem value="">Use target default</MenuItem>
                  {runtimeTemplates.filter((template) => template.isActive).map((template) => (
                    <MenuItem key={template._id} value={template._id}>{template.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select label="Override Config Instance" value={deployForm.runtimeConfigInstanceId} onChange={(event) => setDeployForm((current) => ({ ...current, runtimeConfigInstanceId: event.target.value }))} fullWidth>
                  <MenuItem value="">Use target default</MenuItem>
                  {runtimeInstances.filter((instance) => instance.isActive).map((instance) => (
                    <MenuItem key={instance._id} value={instance._id}>{instance.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <TextField label="Reason / Release Notes" value={deployForm.reason} onChange={(event) => setDeployForm((current) => ({ ...current, reason: event.target.value }))} fullWidth multiline minRows={3} />
            {selectedDeploymentTarget && (
              <Alert severity="info">
                Deploying <strong>{getRepositoryName(selectedDeploymentTarget.repository)}</strong> to{' '}
                <strong>{getCustomerName(selectedDeploymentTarget.customer)}</strong> on{' '}
                <strong>{getServerName(selectedDeploymentTarget.server)}</strong> ({selectedDeploymentTarget.environment})
                {deployForm.deploymentMode ? ` using ${formatDeploymentModeLabel(deployForm.deploymentMode)}` : ''}.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeployDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleDeploySubmit} disabled={saving}>Trigger Deployment</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={restartDialogOpen} onClose={closeRestartDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Restart</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Alert severity="warning">
              This will run <strong>docker compose down</strong> and <strong>docker compose up -d</strong> for{' '}
              <strong>{restartDeploymentRecord ? getRepositoryName(restartDeploymentRecord.repository) : '-'}</strong>.
            </Alert>
            <Alert severity="info">
              Restart bounces the whole compose project for this deployment target, not just a single container.
            </Alert>
            {restartDeploymentRecord && (
              <Typography variant="body2" color="text.secondary">
                Target version: {restartDeploymentRecord.requestedRelease?.displayVersion || '-'} on {restartDeploymentRecord.environment}
                {restartDeploymentRecord.deploymentMode
                  ? ` using ${formatDeploymentModeLabel(restartDeploymentRecord.deploymentMode)}`
                  : selectedRestartTarget && targetSupportsDockerRestart(selectedRestartTarget)
                    ? ' using Docker compose'
                    : ''}
              </Typography>
            )}
            <TextField
              label="Restart Reason (optional)"
              value={restartReason}
              onChange={(event) => setRestartReason(event.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRestartDialog}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRestartSubmit} disabled={saving}>Restart Deployment</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rollbackDialogOpen} onClose={closeRollbackDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Rollback</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Alert severity="warning">
              This will redeploy the previously successful snapshot for{' '}
              <strong>{rollbackDeploymentRecord ? getRepositoryName(rollbackDeploymentRecord.repository) : '-'}</strong>.
            </Alert>
            {rollbackDeploymentMode && (
              <Alert severity="info">
                Rollback mode is locked to the recorded source deployment mode:{' '}
                <strong>{formatDeploymentModeLabel(rollbackDeploymentMode)}</strong>.
              </Alert>
            )}
            {!rollbackDeploymentMode && (selectedRollbackTarget?.allowedDeploymentModes?.length ?? 0) > 0 && (
              <Alert severity="info">
                This deployment predates recorded rollback modes. The backend will decide whether rollback can proceed.
              </Alert>
            )}
            <TextField label="Rollback Reason" value={rollbackReason} onChange={(event) => setRollbackReason(event.target.value)} fullWidth multiline minRows={3} />
            {rollbackDeploymentRecord && (
              <Typography variant="body2" color="text.secondary">
                Target version: {rollbackDeploymentRecord.requestedRelease?.displayVersion || '-'} on {rollbackDeploymentRecord.environment}
                {rollbackDeploymentMode ? ` using ${formatDeploymentModeLabel(rollbackDeploymentMode)}` : ''}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRollbackDialog}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRollbackSubmit} disabled={saving}>Start Rollback</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeploymentDashboard;
